import "./styles.css";

import {
  calculatePublicQuoteFinancials,
  calculateQuoteFinancials,
  classifyEquoCompenso,
  resolveEquoCompensoEngine,
  publicTechnicalExpenseMaximumPercentage,
  type CommissionerKind,
  type Dm2016FootnoteContext,
  type EquoCompensoApplicability,
  type EquoCompensoRoute,
  type EquoCompensoScreeningResult,
  type PublicAwardProcedure,
  type ServiceContext,
  type QuoteFinancialsResult,
  type PublicQuoteFinancialsResult,
} from "@rcprofessionale/calc-core";

import {
  buildVerificationModel,
  type VerificationComponent,
  type VerificationModel,
} from "./verification.js";
import type {
  CorpusGate,
  CorpusGateStatus,
  CorpusReleaseBlocker,
} from "./corpus-status.js";
import {
  validateQuoteDraft,
  type QuoteDraftInput,
} from "./quote-draft.js";
import {
  QUOTE_DOCUMENT_SCHEMA_VERSION,
  DEFAULT_QUOTE_ACCENT_COLOR,
  DEFAULT_QUOTE_PRIMARY_COLOR,
  bindVerifiedCanonicalQuote,
  createVersionedQuoteDocument,
  publicEconomicsToQuoteFinancials,
  type QuoteAdjustmentsInput,
  type QuoteBrandingInput,
  type QuoteCalculationDetailInput,
  type QuoteCalculationContextRow,
  type QuoteTemplateId,
  type VerifiedCalculationBinding,
  type VersionedQuoteDocument,
} from "./versioned-quote.js";
import {
  requestAndVerifyDm2016CanonicalQuote,
  type Dm2016AuthoritativeRecalculationExpectation,
  type Dm2016BundleAuthoritativeRecalculationExpectation,
} from "./authoritative-recalculation-client.js";
import { mountVaultLab, type VaultLabController } from "./vault-lab.js";
import { mountDm2016Lab, type Dm2016LabController } from "./dm2016-lab.js";
import {
  mountDm2016LinkedLab,
  type Dm2016LinkedLabController,
} from "./dm2016-linked-lab.js";
import type {
  HashedDm2016LabCalculationResult,
  HashedDm2016LinkedBundleCalculationResult,
} from "./dm2016-application.js";
import {
  consumeSafeAuthReturnDraftFromBrowser,
  saveSafeAuthReturnDraftForBrowser,
  type SafeAuthReturnDraft,
} from "./auth-return-draft.js";
import {
  INSURANCE_INTAKE_CONSENT_TEXT,
  mountInsuranceIntake,
} from "./insurance-intake.js";
import { buildWhatsappContactUrl } from "./public-contact.js";
import type {
  ProfessionalProfile,
  ProfessionalProfileInput,
  ProfessionalProfileProfession,
} from "./professional-profile.js";

const app = document.querySelector<HTMLDivElement>("#app") ?? (() => {
  throw new Error("Contenitore applicazione non trovato");
})();

interface QuoteCalculationContext {
  readonly calculation: VerifiedCalculationBinding;
  readonly authoritative:
    | {
      readonly kind: "single";
      readonly expectation: Dm2016AuthoritativeRecalculationExpectation;
    }
    | {
      readonly kind: "bundle";
      readonly expectation: Dm2016BundleAuthoritativeRecalculationExpectation;
    };
  readonly components: readonly {
    readonly label: string;
    readonly valueV: string;
    readonly compensationRounded: string;
  }[];
  readonly details: readonly QuoteCalculationDetailInput[];
  readonly label: string;
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function groupInteger(value: string): string {
  return value.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

function formatDecimal(value: string, minimumDecimals = 0): string {
  const [rawInteger = "0", rawDecimals = ""] = value.split(".");
  const negative = rawInteger.startsWith("-");
  const integer = negative ? rawInteger.slice(1) : rawInteger;
  const decimals = rawDecimals.padEnd(minimumDecimals, "0");
  return `${negative ? "−" : ""}${groupInteger(integer)}${
    decimals.length > 0 ? `,${decimals}` : ""
  }`;
}

function formatEuro(value: string): string {
  return `€ ${formatDecimal(value, 2)}`;
}

function contrastHexColor(value: string): string {
  const red = Number.parseInt(value.slice(1, 3), 16) / 255;
  const green = Number.parseInt(value.slice(3, 5), 16) / 255;
  const blue = Number.parseInt(value.slice(5, 7), 16) / 255;
  const luminance = 0.2126 * red + 0.7152 * green + 0.0722 * blue;
  return luminance > 0.58 ? "#122632" : "#ffffff";
}

function shortHash(value: string): string {
  return `${value.slice(0, 10)}…${value.slice(-8)}`;
}

function formatItalianCalendarDate(value: string): string {
  const [year, month, day] = value.split("-");
  if (year === undefined || month === undefined || day === undefined) return value;
  return `${day}/${month}/${year}`;
}

function screeningRouteLabel(route: EquoCompensoRoute): string {
  const labels: Record<EquoCompensoRoute, string> = {
    public_contracts_annex_i13: "Percorso pubblico: art. 41 e Allegato I.13",
    dm140_protected_client_review: "Percorso D.M. 140 in completamento",
    ordinary_negotiated_estimate: "Preventivo ordinario negoziato",
    collection_agent_adequacy_review: "Verifica dedicata di adeguatezza",
    blocked: "Percorso da determinare · calcolo bloccato",
  };
  return labels[route];
}

function screeningApplicabilityLabel(
  applicability: EquoCompensoApplicability,
): string {
  const labels: Record<EquoCompensoApplicability, string> = {
    applies: "Pre-verifica: ambito della Legge 49/2023 individuato",
    does_not_apply: "Preventivo parametrico di riferimento",
    indeterminate: "Applicabilità legale da verificare",
    special_adequacy_rule: "Regola speciale di adeguatezza",
  };
  return labels[applicability];
}

function screeningTone(
  applicability: EquoCompensoApplicability,
  hasBlockers: boolean,
): "ok" | "warning" | "neutral" {
  if (hasBlockers || applicability === "indeterminate") return "warning";
  if (applicability === "applies") return "ok";
  return "neutral";
}

function crosscheckLabel(status: VerificationComponent["crosscheckStatus"]): string {
  if (status === "within_range") return "Riscontro Z-1 coerente";
  if (status === "conflict_requires_approval") return "Conflitto Z-1 da approvare";
  return "Destinazione Z-1 da definire";
}

function crosscheckTone(status: VerificationComponent["crosscheckStatus"]): string {
  return status === "within_range" ? "ok" : "warning";
}

function corpusGateStatusLabel(status: CorpusGateStatus): string {
  if (status === "complete") return "Riscontro completo";
  if (status === "partial") return "Verifica in corso";
  return "Secondo riscontro richiesto";
}

function renderCorpusGate(gate: CorpusGate): string {
  return `
    <article class="corpus-gate corpus-gate--${gate.status}">
      <div class="corpus-gate__head">
        <span>${escapeHtml(gate.table)}</span>
        <small>${escapeHtml(corpusGateStatusLabel(gate.status))}</small>
      </div>
      <h3>${escapeHtml(gate.label)}</h3>
      <div class="corpus-gate__count">
        <strong>${gate.confirmed}<span>/${gate.total}</span></strong>
        <p>${gate.remaining === 0 ? "nessun riscontro residuo" : `${gate.remaining} ancora da confermare`}</p>
      </div>
      <progress max="${gate.total}" value="${gate.confirmed}" aria-label="${escapeHtml(gate.label)}: ${gate.confirmed} di ${gate.total}"></progress>
      <p class="corpus-gate__detail">${escapeHtml(gate.detail)}</p>
    </article>`;
}

function renderReleaseBlocker(blocker: CorpusReleaseBlocker): string {
  const occurrenceLabel = blocker.occurrences === 1
    ? "1 condizione"
    : `${blocker.occurrences} condizioni`;
  return `
    <li class="release-blocker">
      <div>
        <code>${escapeHtml(blocker.code)}</code>
        <strong>${escapeHtml(blocker.label)}</strong>
      </div>
      <p>${escapeHtml(blocker.artifacts.join(" · "))}</p>
      <small>${occurrenceLabel}</small>
    </li>`;
}

function renderComponent(component: VerificationComponent, index: number): string {
  const services = component.services
    .map(
      (service) => `
        <li class="service-row">
          <span class="service-row__code">${escapeHtml(service.code)}</span>
          <span class="service-row__name">${escapeHtml(service.description)}</span>
          <strong>Q ${escapeHtml(formatDecimal(service.qValue))}</strong>
        </li>`,
    )
    .join("");

  return `
    <details class="calculation" ${index === 0 ? "open" : ""}>
      <summary>
        <span class="calculation__index" aria-hidden="true">0${index + 1}</span>
        <span class="calculation__identity">
          <strong>${escapeHtml(component.label)}</strong>
          <small>V ${escapeHtml(formatEuro(component.valueV))}</small>
        </span>
        <span class="calculation__amount">
          <strong>${escapeHtml(formatEuro(component.compensationRounded))}</strong>
          <small class="status status--${crosscheckTone(component.crosscheckStatus)}">
            ${escapeHtml(crosscheckLabel(component.crosscheckStatus))}
          </small>
        </span>
        <span class="calculation__chevron" aria-hidden="true"></span>
      </summary>
      <div class="calculation__body">
        <div class="formula" aria-label="Formula applicata">
          <span>V</span><b>×</b><span>P</span><b>×</b><span>G</span><b>×</b><span>ΣQ</span><b>=</b><strong>CP</strong>
        </div>
        <dl class="metric-grid">
          <div><dt>Valore V</dt><dd>${escapeHtml(formatEuro(component.valueV))}</dd></div>
          <div><dt>Parametro P</dt><dd>${escapeHtml(formatDecimal(component.parameterPPercentDisplay, 2))}%</dd></div>
          <div><dt>Complessità G</dt><dd>${escapeHtml(formatDecimal(component.complexityG))}</dd></div>
          <div><dt>Somma Q</dt><dd>${escapeHtml(formatDecimal(component.qTotal))}</dd></div>
        </dl>
        <div class="trace-line">
          <span>Calcolo non arrotondato</span>
          <code>${escapeHtml(component.compensationRaw)}</code>
        </div>
        <div class="trace-line">
          <span>Risultato dell’esempio ufficiale</span>
          <strong>${escapeHtml(formatEuro(component.expectedCompensation))}</strong>
          <span class="match">${component.matchesOfficialExample ? "Coincide" : "Non coincide"}</span>
        </div>
        <div class="services">
          <div class="section-heading section-heading--compact">
            <div><span class="eyebrow">Prestazioni incluse</span><h3>Composizione del parametro Q</h3></div>
            <small>${component.services.length} voci</small>
          </div>
          <ul>${services}</ul>
        </div>
        <p class="source-note">Fonte categoria: ${escapeHtml(component.sourceLocator)}</p>
      </div>
    </details>`;
}

function render(model: VerificationModel): void {
  const { result, ruleset } = model;
  const publicResult = model.publicTechnical.result;
  const components = model.components.map(renderComponent).join("");
  const corpusGates = model.normativeCorpus.gates.map(renderCorpusGate).join("");
  const releaseBlockers = model.normativeCorpus.certificationReview.blockerGroups
    .map(renderReleaseBlocker)
    .join("");
  const quoteIssueDate = new Date().toISOString().slice(0, 10);
  const whatsappContactUrl = buildWhatsappContactUrl(import.meta.env);
  const whatsappContactLink = whatsappContactUrl === null
    ? ""
    : `<a class="insurance-bridge__whatsapp" href="${escapeHtml(whatsappContactUrl)}" target="_blank" rel="noopener noreferrer" aria-label="Scrivici su WhatsApp">
        <span aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.16-.17.2-.35.22-.64.08-.3-.15-1.26-.46-2.39-1.48-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.21 3.07c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.69.25-1.29.17-1.41-.07-.12-.27-.2-.57-.35M12.05 21.79h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.86 9.86 0 0 1-1.51-5.26c0-5.45 4.44-9.88 9.89-9.88 2.64 0 5.12 1.03 6.99 2.9a9.83 9.83 0 0 1 2.89 6.99c0 5.45-4.44 9.88-9.88 9.88M20.46 3.49A11.82 11.82 0 0 0 12.05 0C5.5 0 .16 5.34.16 11.89c0 2.1.55 4.14 1.59 5.95L.06 24l6.31-1.65a11.88 11.88 0 0 0 5.68 1.45h.01c6.55 0 11.89-5.34 11.89-11.89 0-3.18-1.24-6.16-3.49-8.42"/></svg>
        </span>
        <span><strong>Scrivici su WhatsApp</strong><small>Siamo qui per aiutarti</small></span>
      </a>`;
  let quoteCalculationContext: QuoteCalculationContext | null = null;
  const blockers = ruleset.blocking_issues
    .map(
      (issue) => `
        <li>
          <span class="blocker__code">${escapeHtml(issue.code)}</span>
          <p>${escapeHtml(issue.description)}</p>
        </li>`,
    )
    .join("");

  app.innerHTML = `
    <div class="site-shell">
      <header class="topbar">
        <a class="brand" href="/" aria-label="rcprofessionale.ing — torna alla home">
          <img class="brand__logo" src="/equocompensonow/equocompensonow-mark.svg" alt="" width="48" height="48" />
          <span><strong>EquoCompenso<span>Now</span></strong><small>Preventivatore per tecnici professionali</small></span>
        </a>
        <div class="topbar__actions">
          <span class="lab-pill"><i aria-hidden="true"></i> Equo compenso · quadro 2023</span>
          <button class="auth-trigger" id="auth-trigger" type="button" aria-label="Accedi all’area privata">Accedi</button>
        </div>
      </header>

      <main id="contenuto">
        <section class="hero wrap" aria-labelledby="hero-title">
          <div class="hero__copy">
            <div class="hero__labels"><span class="eyebrow">Preventivatore per l’equo compenso dei tecnici</span><span class="free-pill">Gratuito</span></div>
            <h1 id="hero-title">L’equo compenso,<br /><em>calcolato con chiarezza.</em></h1>
            <p>Preventivi parametrici per ingegneri, architetti, geometri e geologi, nel quadro normativo introdotto nel 2023.</p>
            <div class="hero__actions">
              <a href="#screening-title">Inizia il preventivo</a>
              <span>D.M. 17/06/2016 · Legge 49/2023 · D.Lgs. 36/2023 · correttivo 209/2024 in verifica</span>
            </div>
          </div>
          <aside class="safety-card" aria-label="Stato del motore">
            <div class="safety-card__icon" aria-hidden="true">!</div>
            <div>
              <strong>Motore operativo verificato</strong>
              <p>Il percorso 2023 è applicato dal motore verificato. Il risultato è una stima parametrica tracciabile: controlla sempre dati, prestazioni e applicabilità al singolo incarico.</p>
            </div>
          </aside>
        </section>

        <section class="screening wrap" aria-labelledby="screening-title">
          <div class="screening__intro">
            <span class="eyebrow">1 · Identifica il percorso</span>
            <h2 id="screening-title">A chi è destinato il preventivo?</h2>
            <p>I dati riguardano sempre il committente che affida l’incarico, non il professionista che emette il preventivo.</p>
          </div>
          <div class="screening__body">
            <form class="screening-form" id="screening-form">
              <label>
                Tipo di committente
                <select id="screening-commissioner" name="commissioner">
                  <option value="unknown">Seleziona il committente</option>
                  <option value="private_individual">Privato / persona fisica</option>
                  <option value="private_enterprise">Impresa privata</option>
                  <option value="bank">Banca</option>
                  <option value="insurance_company">Impresa assicurativa</option>
                  <option value="bank_or_insurance_controlled_or_mandated">Società controllata o mandataria di banca/assicurazione</option>
                  <option value="public_administration">Pubblica amministrazione</option>
                  <option value="tusp_company">Società disciplinata dal TUSP (D.Lgs. 175/2016)</option>
                  <option value="securitisation_vehicle">Società veicolo di cartolarizzazione</option>
                  <option value="collection_agent">Agente della riscossione</option>
                </select>
              </label>
              <label>
                Tipo di incarico
                <select id="screening-context" name="context">
                  <option value="unknown">Seleziona il contesto</option>
                  <option value="other_professional_engagement">Incarico professionale non pubblico</option>
                  <option value="public_engineering_architecture_award">Affidamento pubblico di ingegneria/architettura</option>
                </select>
              </label>
              <div class="screening-form__conditional" data-screening-group="enterprise" hidden>
                <div class="screening-form__conditional-heading">
                  <p>Dimensione dell’impresa committente nell’anno precedente</p>
                  <small>Basta un solo “Sì” perché la soglia dimensionale risulti superata. Il calcolo resta disponibile anche scegliendo “Non lo so”.</small>
                </div>
                <label>
                  Aveva più di 50 dipendenti?
                  <select id="screening-employees" name="employees-threshold">
                    <option value="">Non lo so</option>
                    <option value="51">Sì</option>
                    <option value="50">No</option>
                  </select>
                </label>
                <label>
                  Aveva ricavi annui superiori a € 10 milioni?
                  <select id="screening-revenue" name="revenue-threshold">
                    <option value="">Non lo so</option>
                    <option value="10000000.01">Sì</option>
                    <option value="10000000">No</option>
                  </select>
                </label>
              </div>
              <div class="screening-form__conditional" data-screening-group="public-procedure" hidden>
                <label>
                  Procedura di affidamento
                  <select id="screening-procedure" name="procedure">
                    <option value="other_or_unknown">Seleziona la procedura</option>
                    <option value="article_108_2_b_best_value">Offerta economicamente più vantaggiosa — art. 108, c. 2, lett. b)</option>
                    <option value="article_50_1_b_direct_award">Affidamento diretto — art. 50, c. 1, lett. b)</option>
                  </select>
                </label>
              </div>
            </form>
            <section class="screening-result screening-result--warning" id="screening-result" aria-live="polite" aria-labelledby="screening-result-title">
              <span class="screening-result__status" id="screening-status">Applicabilità da determinare</span>
              <h3 id="screening-result-title">Percorso da determinare</h3>
              <p class="screening-result__summary">Seleziona committente, contesto e procedura. Nessun motore viene autorizzato prima dello screening.</p>
              <div class="screening-result__rules" id="screening-rules" hidden></div>
              <ul id="screening-notes"><li>Selezionare il tipo di committente.</li></ul>
              <div class="public-golden" id="public-golden" hidden>
                <div>
                  <span>Caso d’oro pubblico indipendente</span>
                  <strong>${model.publicTechnical.allValuesMatch ? "Controllo superato" : "Controllo fallito"}</strong>
                </div>
                <b>${escapeHtml(formatEuro(publicResult.totals.totalBeforeTaxesAndContributions))}</b>
                <dl>
                  <div><dt>Compenso</dt><dd>${escapeHtml(formatEuro(publicResult.totals.compensationRounded))}</dd></div>
                  <div><dt>BIM 10%</dt><dd>${escapeHtml(formatEuro(publicResult.totals.bimIncrement))}</dd></div>
                  <div><dt>Spese 10%</dt><dd>${escapeHtml(formatEuro(publicResult.totals.expenses))}</dd></div>
                </dl>
                <p>Riproduzione del documento Agenzia del Demanio, non importo applicabile al tuo incarico. È un’evidenza tecnica del gate operativo, non una certificazione legale.</p>
              </div>
              <small>Qualificazione preliminare, non parere legale. I moduli D.M. 2016 restano disabilitati finché lo screening non autorizza espressamente il percorso pubblico.</small>
            </section>
          </div>
        </section>

        <div id="dm2016-lab-root"></div>

        <details class="technical-disclosure technical-disclosure--corpus">
          <summary class="technical-disclosure__summary wrap">
            <span><strong>Stato della verifica normativa</strong><small>${model.normativeCorpus.completedGates}/${model.normativeCorpus.totalGates} controlli completati · calcolo professionale operativo</small></span>
            <b>Mostra i dettagli</b>
          </summary>
        <section class="corpus-status wrap" aria-labelledby="corpus-title">
          <div class="corpus-status__heading">
            <div>
              <span class="eyebrow eyebrow--light">Corpus D.M. 17 giugno 2016</span>
              <h2 id="corpus-title">Le prove del motore restano visibili.</h2>
              <p>Ogni contatore arriva dagli artefatti di trascrizione del progetto. Nessuna cella viene ricostruita per somiglianza e ogni risultato conserva la propria impronta.</p>
            </div>
            <div class="corpus-status__summary" aria-label="Stato complessivo del corpus">
              <span class="corpus-status__signal" aria-hidden="true">✓</span>
              <div>
                <small>Uso professionale</small>
                <strong>Operativo</strong>
                <p>${model.normativeCorpus.completedGates}/${model.normativeCorpus.totalGates} controlli completati · 50/50 scenari pubblici coincidenti</p>
              </div>
            </div>
          </div>
          <div class="corpus-gates">${corpusGates}</div>
          <section class="release-gate-summary" aria-labelledby="release-gate-title">
            <div class="release-gate-summary__heading">
              <div>
                <span>Riesame ulteriore</span>
                <h3 id="release-gate-title">Cosa resta per una futura certificazione formale</h3>
                <p>Queste condizioni non bloccano il calcolo parametrico operativo; impediscono soltanto di presentarlo come parere o certificazione legale automatica.</p>
              </div>
              <strong>${model.normativeCorpus.certificationReview.issueCount}<small>condizioni di certificazione</small></strong>
            </div>
            <details class="release-blockers">
              <summary>Esamina ${model.normativeCorpus.certificationReview.blockerGroups.length} categorie di riesame</summary>
              <ul>${releaseBlockers}</ul>
            </details>
          </section>
          <div class="corpus-status__source">
            <div><span>Fonte comune</span><strong>${escapeHtml(model.normativeCorpus.sourcePublication)}</strong></div>
            <div><span>Impronta SHA-256</span><code title="${escapeHtml(model.normativeCorpus.sourceSha256)}">${escapeHtml(shortHash(model.normativeCorpus.sourceSha256))}</code></div>
            <p>Il calcolo parametrico è operativo sulla base delle evidenze congelate. L’eventuale certificazione formale resta un livello distinto e non viene promessa dal software.</p>
          </div>
        </section>
        </details>

        <details class="technical-disclosure technical-disclosure--example">
          <summary class="technical-disclosure__summary wrap">
            <span><strong>Confronto tecnico con l’esempio ufficiale D.M. 140/2012</strong><small>Quattro componenti, formula, impronte e questioni ancora aperte</small></span>
            <b>Mostra i dettagli</b>
          </summary>
        <section class="workspace wrap" aria-label="Verifica del calcolo">
          <div class="workspace__main">
            <section class="panel calculation-panel" aria-labelledby="detail-title">
              <div class="section-heading">
                <div><span class="eyebrow">Dettaglio verificabile</span><h2 id="detail-title">Quattro componenti, nessuna scorciatoia</h2></div>
                <span class="verified-badge">${model.allValuesMatch ? "4/4 coincidenti" : "Verifica fallita"}</span>
              </div>
              <div class="calculations">${components}</div>
            </section>

            <section class="panel audit-panel" aria-labelledby="audit-title">
              <div class="section-heading">
                <div><span class="eyebrow">Tracciabilità</span><h2 id="audit-title">Carta d’identità del calcolo</h2></div>
                <span class="seal" aria-hidden="true">SHA</span>
              </div>
              <dl class="audit-grid">
                <div><dt>Motore</dt><dd>v${escapeHtml(result.engineVersion)}</dd></div>
                <div><dt>Formula</dt><dd><code>${escapeHtml(result.formulaId)}</code></dd></div>
                <div><dt>Ruleset</dt><dd><code>${escapeHtml(result.rulesetVersion)}</code></dd></div>
                <div><dt>Stato</dt><dd><span class="status status--warning">Bozza bloccata</span></dd></div>
                <div><dt>Impronta input</dt><dd><code title="${escapeHtml(result.inputSha256)}">${escapeHtml(shortHash(result.inputSha256))}</code></dd></div>
                <div><dt>Impronta risultato</dt><dd><code title="${escapeHtml(result.resultSha256)}">${escapeHtml(shortHash(result.resultSha256))}</code></dd></div>
              </dl>
              <details class="blockers">
                <summary>Esamina le ${ruleset.blocking_issues.length} questioni che impediscono l’uso professionale</summary>
                <ul>${blockers}</ul>
              </details>
            </section>
          </div>

          <aside class="workspace__side">
            <section class="result-card" aria-labelledby="result-title">
              <span class="eyebrow eyebrow--light">Risultato riprodotto</span>
              <p class="result-card__overline">Compenso parametrico netto</p>
              <h2 id="result-title">${escapeHtml(formatEuro(result.totals.compensationRounded))}</h2>
              <p>Somma dei quattro importi parziali, arrotondati ai centesimi.</p>
              <dl>
                <div><dt>Valore complessivo V</dt><dd>${escapeHtml(formatEuro(result.totals.valueV))}</dd></div>
                <div><dt>Componenti</dt><dd>${result.components.length}</dd></div>
                <div><dt>Delta arrotondamento</dt><dd>${escapeHtml(formatEuro(result.totals.roundingDelta))}</dd></div>
              </dl>
              <div class="result-card__verified"><span aria-hidden="true">✓</span><p><strong>Coincide con l’esempio</strong><small>Controllo automatico superato</small></p></div>
            </section>

            <section class="roadmap-card" aria-labelledby="roadmap-title">
              <span class="eyebrow">Il prodotto completo · uso gratuito</span>
              <h2 id="roadmap-title">Nessun limite attivo</h2>
              <ol>
                <li class="is-current"><span>01</span><p><strong>Calcolo verificabile</strong><small>Primo caso attivo</small></p></li>
                <li><span>02</span><p><strong>Preventivo personalizzato</strong><small>Struttura e clausole</small></p></li>
                <li><span>03</span><p><strong>PDF white-label</strong><small>Logo e identità studio</small></p></li>
                <li><span>04</span><p><strong>Archivio clienti</strong><small>Progetti e revisioni</small></p></li>
                <li><span>05</span><p><strong>Più normative</strong><small>D.M. 2016 e D.Lgs. 36/2023</small></p></li>
              </ol>
            </section>
          </aside>
        </section>
        </details>

        <section class="quote-lab wrap" aria-labelledby="quote-lab-title">
          <div class="quote-lab__heading">
            <div>
              <span class="eyebrow">3 · Crea il preventivo PDF</span>
              <h2 id="quote-lab-title">Dal calcolo al tuo preventivo professionale.</h2>
              <p>Per singoli professionisti, studi e società tecniche. Il documento usa esclusivamente l’ultimo calcolo D.M. 2016 eseguito sopra.</p>
            </div>
            <div class="quote-lab__privacy">
              <span aria-hidden="true">◇</span>
              <p><strong>I dati restano sotto il tuo controllo.</strong> I dati del cliente non vengono salvati automaticamente. Se lo scegli, soltanto il profilo del mittente viene memorizzato per precompilare i preventivi futuri; PDF e dati del cliente entrano nel cloud esclusivamente cifrati.</p>
            </div>
          </div>

          <section class="profession-card panel quote-profession-card" aria-labelledby="profession-title">
            <div>
              <span class="eyebrow">Intestazione PDF</span>
              <h2 id="profession-title">Scegli la professione.</h2>
              <p>Personalizza il documento, non il calcolo.</p>
            </div>
            <div class="profession-switcher" role="group" aria-label="Seleziona la professione">
              <button type="button" class="profession is-active" data-profession="Ingegnere" aria-pressed="true"><span>In</span> Ingegnere</button>
              <button type="button" class="profession" data-profession="Architetto" aria-pressed="false"><span>Ar</span> Architetto</button>
              <button type="button" class="profession" data-profession="Geometra" aria-pressed="false"><span>Ge</span> Geometra</button>
              <button type="button" class="profession" data-profession="Geologo" aria-pressed="false"><span>Go</span> Geologo</button>
            </div>
            <p class="profession-note"><span aria-hidden="true">✓</span> Selezionata: <strong id="selected-profession">Ingegnere</strong></p>
            <details class="quote-privacy-details"><summary>Come proteggiamo profilo e dati del cliente</summary><p><strong>Il cliente resta tuo.</strong> Denominazione, referente, partita IVA e indirizzo del mittente vengono salvati nel profilo soltanto su comando dell’utente e protetti per proprietario. I dati del destinatario e il PDF restano locali oppure vengono caricati esclusivamente dopo la cifratura nel browser.</p></details>
          </section>

          <div class="quote-builder">
            <form class="quote-form" id="quote-form" novalidate>
              <div class="quote-form__intro"><span>Mittente e destinatario distinti</span><strong>Completa il preventivo</strong><p>I dati del mittente possono essere salvati nell’area privata e riutilizzati. Quelli del cliente restano specifici di questo preventivo.</p></div>
              <fieldset>
                <legend>Mittente · professionista, studio o società</legend>
                <label>Nome o denominazione<input id="quote-studio" name="studioName" maxlength="120" autocomplete="organization" placeholder="Professionista, studio o società" required /></label>
                <label>Professionista o referente<input id="quote-professional" name="professionalName" maxlength="120" autocomplete="name" placeholder="Nome e cognome" required /></label>
                <label>Partita IVA<input id="quote-sender-vat" name="senderVatNumber" maxlength="32" autocomplete="off" placeholder="Es. IT12345678901" /></label>
                <label>Indirizzo del mittente<textarea id="quote-sender-address" name="senderAddress" maxlength="300" rows="2" autocomplete="street-address" placeholder="Via, numero civico, CAP, città"></textarea></label>
                <div class="quote-profile-tools quote-form__wide" data-profile-tools>
                  <div><strong>I tuoi dati riutilizzabili</strong><span data-profile-status>Accedi all’area privata per salvarli una sola volta.</span></div>
                  <button type="button" data-profile-save disabled>Salva i miei dati</button>
                </div>
              </fieldset>
              <fieldset>
                <legend>Destinatario · cliente o committente</legend>
                <label class="quote-form__wide">Nome o denominazione<input id="quote-client" name="clientName" maxlength="160" autocomplete="off" placeholder="Committente dimostrativo" required /></label>
                <label>Partita IVA<input id="quote-client-vat" name="clientVatNumber" maxlength="32" autocomplete="off" placeholder="Se applicabile" /></label>
                <label>Indirizzo del destinatario<textarea id="quote-client-address" name="clientAddress" maxlength="300" rows="2" autocomplete="off" placeholder="Via, numero civico, CAP, città"></textarea></label>
              </fieldset>
              <fieldset>
                <legend>Oggetto</legend>
                <label class="quote-form__wide">Oggetto dell’incarico<input id="quote-subject" name="projectSubject" maxlength="240" autocomplete="off" placeholder="Descrizione sintetica della prestazione" required /></label>
              </fieldset>
              <details class="quote-form__options"><summary><span>Personalizza grafica e logo</span><small>Facoltativo</small></summary><fieldset>
                  <legend>Identità visiva del PDF</legend>
                  <label class="quote-form__wide">Modello grafico
                    <select id="quote-template" name="templateId">
                      <option value="editorial_light">SaaS moderno · consigliato</option>
                      <option value="brand_header">Fascia colore · compatto</option>
                    </select>
                  </label>
                  <label class="quote-form__wide quote-logo-field">Logo del mittente
                    <input id="quote-logo" name="studioLogo" type="file" accept="image/png,image/jpeg,.png,.jpg,.jpeg" />
                    <span>PNG o JPEG, massimo 2 MB. Resta sul dispositivo e viene incorporato soltanto nel PDF.</span>
                  </label>
                  <label class="quote-color-field">Colore principale<span><input id="quote-primary-color" name="primaryColor" type="color" value="${DEFAULT_QUOTE_PRIMARY_COLOR}" /><output data-color-output="primaryColor">${DEFAULT_QUOTE_PRIMARY_COLOR}</output></span></label>
                  <label class="quote-color-field">Colore di accento<span><input id="quote-accent-color" name="accentColor" type="color" value="${DEFAULT_QUOTE_ACCENT_COLOR}" /><output data-color-output="accentColor">${DEFAULT_QUOTE_ACCENT_COLOR}</output></span></label>
                </fieldset></details>
              <details class="quote-form__options"><summary><span>Data, validità e condizioni</span><small>Precompilato</small></summary><fieldset>
                  <legend>Dettagli del documento</legend>
                  <label>Luogo dell’opera<input id="quote-location" name="projectLocation" maxlength="240" autocomplete="off" placeholder="Comune o località" /></label>
                  <label>Data di emissione<input id="quote-issue-date" name="issueDate" type="date" value="${quoteIssueDate}" required /></label>
                  <label>Validità in giorni<input id="quote-validity" name="validityDays" type="number" min="1" max="365" step="1" value="30" required /></label>
                  <label class="quote-form__wide">Condizioni di pagamento<textarea id="quote-payment" name="paymentTerms" maxlength="600" rows="2" placeholder="Esempio: 30% all’incarico, saldo alla consegna"></textarea></label>
                  <label class="quote-form__wide">Note<textarea id="quote-notes" name="notes" maxlength="1200" rows="3" placeholder="Clausole e precisazioni del preventivo"></textarea></label>
                </fieldset></details>
              <details class="quote-form__options"><summary><span>Sconto, spese, contributi e IVA</span><small>Valori modificabili</small></summary><fieldset>
                  <legend>Composizione economica</legend>
                  <label><span id="quote-reduction-label">Ribasso sulla quota assoggettabile (%)</span><input name="discountPercentage" inputmode="decimal" value="0" autocomplete="off" /></label>
                  <label class="quote-form__wide"><input name="bimMandatory" type="checkbox" /> Applica incremento BIM obbligatorio 10%</label>
                  <label>Importo complessivo dell'opera per il limite spese (€)<input name="worksValueForExpenseLimit" inputmode="decimal" value="" placeholder="Seleziona e dichiara l'importo" autocomplete="off" required /></label>
                  <label>Spese generali (%)<input name="expensesPercentage" inputmode="decimal" value="0" autocomplete="off" /></label>
                  <label>Contributo previdenziale (%)<input name="socialSecurityPercentage" inputmode="decimal" value="4" autocomplete="off" /></label>
                  <label>IVA (%)<input name="vatPercentage" inputmode="decimal" value="22" autocomplete="off" /></label>
                  <label>Voce aggiuntiva<input name="additionalItemLabel" maxlength="200" autocomplete="off" placeholder="Es. rilievo aggiuntivo" /></label>
                  <label>Importo voce (€)<input name="additionalItemAmount" inputmode="decimal" autocomplete="off" placeholder="0,00" /></label>
                  <p class="quote-form__wide quote-form__financial-note">Le percentuali sono impostate dal professionista e restano separate dal compenso verificato dal motore.</p>
                </fieldset></details>
              <div class="quote-form__status" id="quote-validation" role="status" aria-live="polite">Completa i campi obbligatori per abilitare la stampa.</div>
              <button class="quote-print-button" id="quote-print" type="button" disabled>Scarica il PDF sul dispositivo</button>
              <small>Il documento viene composto sul dispositivo dal risultato verificato. Nessun totale scritto nella pagina viene considerato attendibile.</small>
            </form>

            <details class="quote-preview-disclosure" open>
              <summary><span>Anteprima del preventivo</span><small>Mostra o nascondi il documento</small></summary>
              <article class="quote-sheet quote-sheet--editorial" id="quote-print-sheet" aria-label="Anteprima del preventivo parametrico">
              <div class="quote-sheet__watermark">FACSIMILE NON VERIFICATO</div>
              <header class="quote-sheet__header">
                <div class="quote-sheet__identity">
                  <div class="quote-sheet__logo" data-quote-logo hidden><img id="quote-preview-logo" alt="Logo del mittente" /></div>
                  <div><span>Preventivo professionale</span><strong id="quote-preview-brand">Mittente da completare</strong></div>
                </div>
                <dl>
                  <div><dt>Numero</dt><dd id="quote-preview-number">PN-LAB-IN-ATTESA</dd></div>
                  <div><dt>Emissione</dt><dd id="quote-preview-issue">${formatItalianCalendarDate(quoteIssueDate)}</dd></div>
                  <div><dt>Validità</dt><dd id="quote-preview-expiry">30 giorni</dd></div>
                </dl>
              </header>
              <section class="quote-sheet__parties">
                <div><span>Mittente</span><strong id="quote-preview-studio">Mittente da completare</strong><p id="quote-preview-professional">Professionista o referente da completare</p><p id="quote-preview-sender-details">Indirizzo e partita IVA non indicati</p></div>
                <div><span>Destinatario</span><strong id="quote-preview-client">Cliente da completare</strong><p id="quote-preview-address">Indirizzo e partita IVA non indicati</p></div>
              </section>
              <section class="quote-sheet__subject">
                <span>Oggetto dell’incarico</span>
                <h3 id="quote-preview-subject">Oggetto da completare</h3>
                <p id="quote-preview-location">Luogo non indicato</p>
              </section>
              <section class="quote-sheet__calculation">
                <div class="quote-sheet__section-title"><span>Compenso verificato</span><small id="quote-preview-calculation-label">Esegui prima il calcolo D.M. 2016</small></div>
                <ul id="quote-preview-components"></ul>
                <dl class="quote-sheet__normative-context" id="quote-preview-context" hidden></dl>
                <table class="quote-sheet__financials">
                  <thead><tr><th>Voce economica</th><th>Parametro</th><th>Importo</th></tr></thead>
                  <tbody>
                    <tr><th>Compenso parametrico verificato</th><td>Motore verificato</td><td id="quote-preview-base">Da ricalcolare</td></tr>
                    <tr data-quote-bim-row hidden><th>Incremento BIM obbligatorio</th><td>10%</td><td id="quote-preview-bim">€ 0,00</td></tr>
                    <tr data-quote-fixed-row hidden><th>Quota fissa</th><td>65%</td><td id="quote-preview-fixed">€ 0,00</td></tr>
                    <tr data-quote-discountable-row hidden><th>Quota assoggettabile a ribasso</th><td>35%</td><td id="quote-preview-discountable">€ 0,00</td></tr>
                    <tr data-row-discount><th id="quote-preview-discount-label">Riduzione</th><td id="quote-preview-discount-rate">0%</td><td id="quote-preview-discount">− € 0,00</td></tr>
                    <tr data-quote-additional-row hidden><th id="quote-preview-additional-label">Voce aggiuntiva</th><td>Importo fisso</td><td id="quote-preview-additional">€ 0,00</td></tr>
                    <tr><th>Spese generali</th><td id="quote-preview-expenses-rate">0%</td><td id="quote-preview-expenses">€ 0,00</td></tr>
                    <tr><th>Contributo previdenziale</th><td id="quote-preview-social-rate">4%</td><td id="quote-preview-social-security">€ 0,00</td></tr>
                    <tr><th>IVA</th><td id="quote-preview-vat-rate">22%</td><td id="quote-preview-vat">€ 0,00</td></tr>
                  </tbody>
                  <tfoot><tr><th colspan="2">Totale preventivo</th><td id="quote-preview-grand-total">Da ricalcolare</td></tr></tfoot>
                </table>
                <p>Le percentuali fiscali e previdenziali sono dichiarate dal professionista e devono essere verificate rispetto al suo regime.</p>
              </section>
              <section class="quote-sheet__terms">
                <div><span>Condizioni di pagamento</span><p id="quote-preview-payment">Da definire</p></div>
                <div><span>Note</span><p id="quote-preview-notes">Nessuna nota inserita.</p></div>
              </section>
              <footer class="quote-sheet__footer">
                <p><strong>Stima parametrica verificata.</strong> Verificare input, prestazioni e applicabilità al singolo incarico.</p>
                <dl>
                  <div><dt>Schema documento</dt><dd>v${QUOTE_DOCUMENT_SCHEMA_VERSION}</dd></div>
                  <div><dt>Motore</dt><dd id="quote-preview-engine">—</dd></div>
                  <div><dt>Ruleset</dt><dd id="quote-preview-ruleset">—</dd></div>
                  <div class="quote-sheet__fingerprint"><dt>Impronta calcolo</dt><dd id="quote-preview-fingerprint">—</dd></div>
                </dl>
              </footer>
              </article>
            </details>
          </div>
        </section>

        <section class="vault-lab wrap" id="vault-lab" aria-label="Archivio privato dei preventivi">
          <div class="auth-vault-bridge" id="auth-vault-bridge" aria-live="polite"></div>
          <div id="vault-lab-root"></div>
        </section>

        <section class="insurance-bridge wrap" aria-labelledby="insurance-title">
          <div class="insurance-bridge__halo insurance-bridge__halo--top" aria-hidden="true"></div>
          <div class="insurance-bridge__halo insurance-bridge__halo--bottom" aria-hidden="true"></div>
          <div class="insurance-bridge__content">
            <span class="eyebrow eyebrow--light">RC per professionisti e studi</span>
            <h2 id="insurance-title">Dalla firma individuale<br />al grande studio.</h2>
            <p class="insurance-bridge__statement"><em>La copertura deve seguire<br />la responsabilità reale.</em></p>

            <div class="insurance-bridge__divider" aria-hidden="true"></div>

            <div class="insurance-bridge__question">
              <h3>Hai già una polizza di assicurazione<br /> RC professionale?</h3>
              <p>Scegli l’opzione più adatta alle tue esigenze.</p>
            </div>

            <div class="insurance-bridge__actions">
              <button class="insurance-action insurance-action--compare" type="button" data-insurance-action="policy">
                <span class="insurance-action__icon" aria-hidden="true">
                  <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M10 5h18l7 7v13"/><path d="M28 5v8h8"/><path d="M10 5v34h18"/><circle cx="32" cy="32" r="8"/><path d="m38 38 6 6"/><path d="M16 20h10"/><path d="M16 26h7"/></svg>
                </span>
                <span class="insurance-action__copy">
                  <small>Sì, ho già una polizza</small>
                  <strong>Confronta la mia polizza</strong>
                  <span>Carica la polizza e richiedi un’analisi delle coperture e un confronto con una nuova proposta.</span>
                </span>
                <span class="insurance-action__arrow" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m13 6 6 6-6 6"/></svg>
                </span>
              </button>

              <button class="insurance-action insurance-action--quote" type="button" data-insurance-action="callback">
                <span class="insurance-action__icon" aria-hidden="true">
                  <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M24 5 40 11v11c0 10-6.5 17-16 21-9.5-4-16-11-16-21V11L24 5Z"/><path d="m17 24 5 5 10-11"/></svg>
                </span>
                <span class="insurance-action__copy">
                  <small>No, non ho una polizza</small>
                  <strong>Richiedi un preventivo<br />RC Professionale</strong>
                  <span>Ricevi una proposta dedicata alla tua attività, al tuo studio o alla tua società.</span>
                </span>
                <span class="insurance-action__arrow" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m13 6 6 6-6 6"/></svg>
                </span>
              </button>
            </div>

            <div class="insurance-bridge__footer">
              <div class="insurance-bridge__privacy">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="5" y="10" width="14" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></svg>
                <p><strong>Servizio assicurativo facoltativo e separato.</strong><br />Solo la polizza inviata volontariamente potrà essere trattata dai nostri operatori autorizzati, dopo i controlli di sicurezza.</p>
              </div>
              ${whatsappContactLink}
            </div>
          </div>
        </section>

        <section class="principles wrap" aria-labelledby="principles-title">
          <div class="principles__intro">
            <span class="eyebrow">Promessa di prodotto</span>
            <h2 id="principles-title">Semplice davanti.<br />Rigoroso dietro.</h2>
          </div>
          <div class="principle-grid">
            <article><span>01</span><h3>Verificabile</h3><p>Ogni totale conserva input, coefficienti, formula, versione e impronta.</p></article>
            <article><span>02</span><h3>Professionale</h3><p>Dal calcolo nasce un preventivo dello studio, non un foglio impersonale.</p></article>
            <article><span>03</span><h3>Indipendente</h3><p>Ogni norma ha regole separate; nessun aggiornamento modifica i vecchi documenti.</p></article>
            <article><span>04</span><h3>Uso gratuito</h3><p>Nessun prezzo, abbonamento o limite commerciale attivo. Il percorso assicurativo resta sempre facoltativo e separato.</p></article>
          </div>
        </section>
      </main>

      <footer class="footer wrap">
        <p><strong>EquoCompensoNow</strong> · preventivatore parametrico per tecnici</p>
        <nav aria-label="Informazioni legali"><a href="/terms-of-use.html">Termini</a><a href="/privacy-policy.html">Privacy</a><a href="/cookie-policy.html">Cookie</a></nav>
        <p>Fonti, versioni e modalità interpretativa sono riportate nel calcolo e nel documento verificato.</p>
      </footer>

      <dialog class="insurance-dialog" id="insurance-dialog" aria-labelledby="insurance-dialog-title">
        <div class="dialog-head">
          <div><span class="eyebrow">RC per professionisti e studi</span><h2 id="insurance-dialog-title">Parliamo della tua copertura</h2></div>
          <button class="dialog-close" type="button" aria-label="Chiudi">×</button>
        </div>
        <p class="dialog-intro" data-insurance-intro>La richiesta è aperta a singoli professionisti, studi associati, STP e società tecniche ed è indipendente dal calcolatore gratuito. I dati assicurativi vengono acquisiti soltanto dopo la verifica di sicurezza.</p>
        <div class="dialog-tabs" role="tablist" aria-label="Tipo di richiesta">
          <button type="button" role="tab" aria-selected="true" data-dialog-tab="callback">Richiamata</button>
          <button type="button" role="tab" aria-selected="false" data-dialog-tab="policy">Carica polizza</button>
        </div>
        <form class="insurance-form" data-dialog-panel="callback" data-insurance-form="callback">
          <label>Numero di cellulare / WhatsApp<input name="phone" type="tel" inputmode="tel" autocomplete="tel" placeholder="Es. 333 123 4567" required /></label>
          <label>Quando preferisci essere contattato?<select name="time" required><option value="">Seleziona una fascia</option><option value="morning">Mattina, 09:00–12:30</option><option value="afternoon">Pomeriggio, 14:30–18:30</option></select></label>
          <label class="check"><input name="whatsapp" type="checkbox" /><span>Preferisco essere ricontattato via WhatsApp</span></label>
          <label class="check"><input name="insurance_privacy" type="checkbox" required /><span>${escapeHtml(INSURANCE_INTAKE_CONSENT_TEXT)} <a href="/privacy-policy.html">Privacy Policy</a></span></label>
          <button class="form-submit" type="submit">Invia richiesta di contatto</button>
        </form>
        <form class="insurance-form" data-dialog-panel="policy" data-insurance-form="policy" hidden>
          <label>Email professionale<input name="email" type="email" autocomplete="email" placeholder="nome@studio.it" required /></label>
          <label class="file-field">Polizza RC attuale in PDF<input name="policy" type="file" accept="application/pdf,.pdf" required /><span>Massimo 10 MB. Il documento è separato dall’archivio preventivi e sarà accessibile soltanto agli operatori assicurativi autorizzati, dopo il controllo di sicurezza.</span></label>
          <label class="check"><input name="policy_privacy" type="checkbox" required /><span>${escapeHtml(INSURANCE_INTAKE_CONSENT_TEXT)} <a href="/privacy-policy.html">Privacy Policy</a></span></label>
          <button class="form-submit" type="submit">Richiedi l’analisi della polizza</button>
        </form>
        <div class="insurance-turnstile" data-insurance-turnstile aria-label="Verifica di sicurezza"></div>
        <div class="form-outcome" role="status" aria-live="polite" hidden></div>
      </dialog>

      <dialog class="auth-dialog" id="auth-dialog" aria-labelledby="auth-dialog-title">
        <div class="dialog-head">
          <div><span class="eyebrow">Area privata</span><h2 id="auth-dialog-title">Accedi senza perdere tempo</h2></div>
          <button class="dialog-close" type="button" aria-label="Chiudi" data-auth-close>×</button>
        </div>
        <div data-auth-unconfigured hidden>
          <div class="auth-unconfigured" role="note">
            <strong>Collegamento cloud non ancora attivo</strong>
            <p>I flussi di accesso e recupero sono pronti nel codice, ma nessun account Supabase remoto è stato collegato e nessun dato viene inviato.</p>
          </div>
        </div>
        <div data-auth-signed-out>
          <button class="auth-google" type="button" data-auth-google>
            <span aria-hidden="true">G</span><strong>Continua con Google</strong>
          </button>
          <div class="auth-separator"><span>oppure ricevi un link via email</span></div>
          <form class="auth-form" data-auth-form="otp">
            <label>Email professionale<input name="email" type="email" autocomplete="email" placeholder="nome@studio.it" required /></label>
            <button type="submit">Inviami il link di accesso</button>
          </form>
          <details class="auth-password">
            <summary>Preferisci email e password?</summary>
            <form class="auth-form" data-auth-form="password">
              <label>Email<input name="email" type="email" autocomplete="email" required /></label>
              <label>Password<input name="password" type="password" minlength="10" autocomplete="current-password" required /><span>Almeno 10 caratteri.</span></label>
              <div class="auth-password__actions">
                <button type="submit" value="signin">Accedi</button>
                <button class="auth-secondary" type="submit" value="signup">Crea account</button>
              </div>
              <button class="auth-reset-link" type="button" data-auth-reset>Password dimenticata?</button>
            </form>
          </details>
          <p class="auth-privacy">La sessione identifica il proprietario dell’archivio. Durante il ritorno da Google, la bozza tecnica e i soli campi del mittente restano per massimo 20 minuti in questo browser; dati del cliente, logo e note sono esclusi. La password dell’archivio resta distinta dall’accesso e non viene inviata alla piattaforma. Premendo “Accedi / crea account” accetti i <a href="/terms-of-use.html">Termini di utilizzo</a> e dichiari di aver letto la <a href="/privacy-policy.html">Privacy Policy</a>.</p>
        </div>
        <div class="auth-recovery" data-auth-recovery hidden>
          <span class="auth-recovery__mark" aria-hidden="true">↻</span>
          <h3>Crea una nuova password</h3>
          <p>Il link ricevuto ha aperto una sessione temporanea verificata. La password deve avere almeno 10 caratteri.</p>
          <form class="auth-form" data-auth-form="recovery">
            <label>Nuova password<input name="password" type="password" minlength="10" maxlength="256" autocomplete="new-password" required /></label>
            <label>Ripeti la password<input name="password_confirmation" type="password" minlength="10" maxlength="256" autocomplete="new-password" required /></label>
            <button type="submit">Aggiorna password</button>
          </form>
          <button type="button" class="auth-secondary auth-recovery__cancel" data-auth-recovery-cancel>Annulla e torna all’accesso</button>
        </div>
        <div class="auth-account" data-auth-signed-in hidden>
          <span class="auth-account__mark" aria-hidden="true">✓</span>
          <h3>Accesso effettuato</h3>
          <p data-auth-email></p>
          <button type="button" class="auth-secondary" data-auth-signout>Esci da questo dispositivo</button>
        </div>
        <div class="auth-outcome" data-auth-outcome role="status" aria-live="polite" hidden></div>
      </dialog>
    </div>`;

  document.querySelectorAll<HTMLButtonElement>(".profession").forEach((button) => {
    button.addEventListener("click", () => {
      document.querySelectorAll<HTMLButtonElement>(".profession").forEach((item) => {
        const active = item === button;
        item.classList.toggle("is-active", active);
        item.setAttribute("aria-pressed", String(active));
      });
      const label = button.dataset.profession;
      const selected = document.querySelector<HTMLElement>("#selected-profession");
      if (label !== undefined && selected !== null) selected.textContent = label;
      const quoteProfession = document.querySelector<HTMLElement>("#quote-preview-professional");
      const professionalName = document.querySelector<HTMLInputElement>("#quote-professional")?.value.trim();
      if (label !== undefined && quoteProfession !== null) {
        quoteProfession.textContent = `${label} · ${professionalName || "Professionista da completare"}`;
      }
    });
  });

  const quotePreviewDisclosure = document.querySelector<HTMLDetailsElement>(
    ".quote-preview-disclosure",
  );
  if (window.matchMedia?.("(max-width: 1020px)").matches === true) {
    quotePreviewDisclosure?.removeAttribute("open");
  }

  const quoteForm = document.querySelector<HTMLFormElement>("#quote-form");
  const quotePrintButton = document.querySelector<HTMLButtonElement>("#quote-print");
  const quoteValidation = document.querySelector<HTMLElement>("#quote-validation");
  const vaultLabRoot = document.querySelector<HTMLElement>("#vault-lab-root");
  const vaultLab: VaultLabController | null = vaultLabRoot === null
    ? null
    : mountVaultLab(vaultLabRoot);
  let currentQuoteDocument: VersionedQuoteDocument | null = null;
  let quoteLogoDataUrl: string | null = null;
  let quoteLogoError: string | null = null;

  const profileStatus = document.querySelector<HTMLElement>("[data-profile-status]");
  const profileSaveButton = document.querySelector<HTMLButtonElement>("[data-profile-save]");

  const quoteField = (name: string): HTMLInputElement | HTMLTextAreaElement | null => {
    if (quoteForm === null) return null;
    const field = quoteForm.elements.namedItem(name);
    return field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement
      ? field
      : null;
  };

  const selectedProfileProfession = (): ProfessionalProfileProfession => {
    const selected = document.querySelector<HTMLButtonElement>(".profession.is-active")?.dataset.profession;
    return ["Ingegnere", "Architetto", "Geometra", "Geologo"].includes(selected ?? "")
      ? selected as ProfessionalProfileProfession
      : "Ingegnere";
  };

  const readProfessionalProfileInput = (): ProfessionalProfileInput => ({
    studioName: quoteField("studioName")?.value ?? "",
    professionalName: quoteField("professionalName")?.value ?? "",
    vatNumber: quoteField("senderVatNumber")?.value ?? "",
    address: quoteField("senderAddress")?.value ?? "",
    profession: selectedProfileProfession(),
  });

  const applyProfessionalProfile = (profile: ProfessionalProfile): boolean => {
    const senderFields = ["studioName", "professionalName", "senderVatNumber", "senderAddress"];
    if (senderFields.some((name) => (quoteField(name)?.value.trim().length ?? 0) > 0)) return false;
    const values: Readonly<Record<string, string>> = {
      studioName: profile.studioName,
      professionalName: profile.professionalName,
      senderVatNumber: profile.vatNumber,
      senderAddress: profile.address,
    };
    Object.entries(values).forEach(([name, value]) => {
      const field = quoteField(name);
      if (field !== null) field.value = value;
    });
    document.querySelectorAll<HTMLButtonElement>(".profession").forEach((button) => {
      if (button.dataset.profession === profile.profession) button.click();
    });
    updateQuoteDraft();
    return true;
  };

  const readQuoteDraftInput = (): QuoteDraftInput | null => {
    if (quoteForm === null) return null;
    const value = (name: string): string => {
      const field = quoteForm.elements.namedItem(name);
      return field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement
        ? field.value
        : "";
    };
    return {
      studioName: value("studioName"),
      professionalName: value("professionalName"),
      senderVatNumber: value("senderVatNumber"),
      senderAddress: value("senderAddress"),
      clientName: value("clientName"),
      clientVatNumber: value("clientVatNumber"),
      clientAddress: value("clientAddress"),
      projectSubject: value("projectSubject"),
      projectLocation: value("projectLocation"),
      issueDate: value("issueDate"),
      validityDays: value("validityDays"),
      paymentTerms: value("paymentTerms"),
      notes: value("notes"),
    };
  };

  const setQuotePreviewText = (id: string, value: string): void => {
    const element = document.querySelector<HTMLElement>(`#${id}`);
    if (element !== null) element.textContent = value;
  };

  const partyDetails = (address: string, vatNumber: string): string => {
    const values = [address.trim(), vatNumber.trim().length === 0 ? "" : `P. IVA ${vatNumber.trim()}`]
      .filter((value) => value.length > 0);
    return values.length === 0 ? "Indirizzo e partita IVA non indicati" : values.join(" · ");
  };

  const renderQuoteCalculationContext = (): void => {
    const componentList = document.querySelector<HTMLUListElement>(
      "#quote-preview-components",
    );
    const contextList = document.querySelector<HTMLDListElement>(
      "#quote-preview-context",
    );
    if (quoteCalculationContext === null) {
      setQuotePreviewText(
        "quote-preview-calculation-label",
        "Dati del calcolo modificati · esegui nuovamente il calcolo",
      );
      setQuotePreviewText("quote-preview-base", "Da ricalcolare");
      setQuotePreviewText("quote-preview-grand-total", "Da ricalcolare");
      setQuotePreviewText("quote-preview-number", "PN-LAB-IN-ATTESA");
      setQuotePreviewText("quote-preview-engine", "—");
      setQuotePreviewText("quote-preview-ruleset", "—");
      setQuotePreviewText("quote-preview-fingerprint", "—");
      componentList?.replaceChildren();
      if (contextList !== null) {
        contextList.replaceChildren();
        contextList.hidden = true;
      }
      return;
    }

    const { calculation, components: boundComponents } = quoteCalculationContext;
    setQuotePreviewText(
      "quote-preview-calculation-label",
      `${quoteCalculationContext.label} · valore vincolato all’impronta`,
    );
    setQuotePreviewText(
      "quote-preview-number",
      `PN-LAB-${calculation.resultSha256.slice(0, 8).toUpperCase()}`,
    );
    setQuotePreviewText("quote-preview-engine", `v${calculation.engineVersion}`);
    setQuotePreviewText("quote-preview-ruleset", calculation.rulesetVersion);
    setQuotePreviewText("quote-preview-fingerprint", calculation.resultSha256);
    componentList?.replaceChildren(...boundComponents.map((component) => {
      const item = document.createElement("li");
      const label = document.createElement("span");
      const value = document.createElement("small");
      const amount = document.createElement("strong");
      label.textContent = component.label;
      value.textContent = `V ${formatEuro(component.valueV)}`;
      amount.textContent = formatEuro(component.compensationRounded);
      item.append(label, value, amount);
      return item;
    }));
    if (contextList !== null) {
      const details = quoteCalculationContext.details;
      const rows = details.flatMap((detail) => [
        ...detail.contextRows.map((row) => ({
          label: details.length === 1 ? row.label : `${detail.serviceCode} · ${row.label}`,
          value: row.value,
        })),
        ...(detail.appliedFootnotes.length === 0
          ? []
          : [{
            label: details.length === 1
              ? "Note D.M. 2016"
              : `${detail.serviceCode} · note D.M. 2016`,
            value: detail.appliedFootnotes.map((item) => `n. ${item}`).join(", "),
          }]),
      ]);
      contextList.replaceChildren(...rows.flatMap((row) => {
        const term = document.createElement("dt");
        const definition = document.createElement("dd");
        term.textContent = row.label;
        definition.textContent = row.value;
        return [term, definition];
      }));
      contextList.hidden = rows.length === 0;
    }
    refreshQuoteFinancialPreview();
  };

  const normalizeDecimalInput = (value: string): string => value.trim().replace(",", ".");

  const readQuoteAdjustments = (): QuoteAdjustmentsInput | null => {
    if (quoteForm === null) return null;
    const value = (name: string): string => {
      const field = quoteForm.elements.namedItem(name);
      return field instanceof HTMLInputElement ? field.value : "";
    };
    const additionalLabel = value("additionalItemLabel").trim();
    const additionalAmount = normalizeDecimalInput(value("additionalItemAmount"));
    if ((additionalLabel.length === 0) !== (additionalAmount.length === 0)) {
      throw new Error("Completa sia la descrizione sia l’importo della voce aggiuntiva.");
    }
    return {
      discountPercentage: normalizeDecimalInput(value("discountPercentage")),
      expensesPercentage: normalizeDecimalInput(value("expensesPercentage")),
      socialSecurityPercentage: normalizeDecimalInput(value("socialSecurityPercentage")),
      vatPercentage: normalizeDecimalInput(value("vatPercentage")),
      additionalItems: additionalLabel.length === 0
        ? []
        : [{ itemId: "additional-1", label: additionalLabel, amount: additionalAmount }],
    };
  };

  const renderQuoteFinancialPreview = (
    financials: QuoteFinancialsResult,
    publicEconomics: PublicQuoteFinancialsResult | null = null,
  ): void => {
    setQuotePreviewText(
      "quote-preview-base",
      formatEuro(financials.totals.verifiedCompensation),
    );
    setQuotePreviewText(
      "quote-preview-discount",
      `− ${formatEuro(financials.totals.discount)}`,
    );
    setQuotePreviewText(
      "quote-preview-discount-rate",
      `${formatDecimal(financials.normalizedInput.discountPercentage)}%`,
    );
    setQuotePreviewText(
      "quote-preview-expenses",
      formatEuro(financials.totals.expenses),
    );
    setQuotePreviewText(
      "quote-preview-expenses-rate",
      `${formatDecimal(financials.normalizedInput.expensesPercentage)}%`,
    );
    setQuotePreviewText(
      "quote-preview-social-security",
      formatEuro(financials.totals.socialSecurityContribution),
    );
    setQuotePreviewText(
      "quote-preview-social-rate",
      `${formatDecimal(financials.normalizedInput.socialSecurityPercentage)}%`,
    );
    setQuotePreviewText("quote-preview-vat", formatEuro(financials.totals.vat));
    setQuotePreviewText(
      "quote-preview-vat-rate",
      `${formatDecimal(financials.normalizedInput.vatPercentage)}%`,
    );
    setQuotePreviewText(
      "quote-preview-grand-total",
      formatEuro(financials.totals.grandTotal),
    );
    const bimRow = document.querySelector<HTMLElement>("[data-quote-bim-row]");
    const fixedRow = document.querySelector<HTMLElement>("[data-quote-fixed-row]");
    const discountableRow = document.querySelector<HTMLElement>(
      "[data-quote-discountable-row]",
    );
    const isArticle108 = publicEconomics?.normalizedInput.procedure
      === "article_108_2_b_best_value";
    if (bimRow !== null) {
      bimRow.hidden = publicEconomics === null
        || publicEconomics.totals.bimIncrement === "0.00";
    }
    if (fixedRow !== null) fixedRow.hidden = !isArticle108;
    if (discountableRow !== null) discountableRow.hidden = !isArticle108;
    if (publicEconomics !== null) {
      setQuotePreviewText("quote-preview-bim", formatEuro(publicEconomics.totals.bimIncrement));
      setQuotePreviewText("quote-preview-fixed", formatEuro(publicEconomics.totals.fixedShare65));
      setQuotePreviewText(
        "quote-preview-discountable",
        formatEuro(publicEconomics.totals.discountableShare35),
      );
      setQuotePreviewText(
        "quote-preview-discount-label",
        isArticle108 ? "Ribasso sulla quota 35%" : "Riduzione affidamento diretto",
      );
    } else {
      setQuotePreviewText("quote-preview-discount-label", "Sconto sul compenso");
    }
    const additionalRow = document.querySelector<HTMLElement>(
      "[data-quote-additional-row]",
    );
    const additionalItem = financials.normalizedInput.additionalItems[0];
    if (additionalRow !== null) additionalRow.hidden = additionalItem === undefined;
    if (additionalItem !== undefined) {
      setQuotePreviewText("quote-preview-additional-label", additionalItem.label);
      setQuotePreviewText("quote-preview-additional", formatEuro(additionalItem.amount));
    }
  };

  const calculateCurrentPublicEconomics = (
    adjustments: QuoteAdjustmentsInput,
  ): PublicQuoteFinancialsResult | null => {
    if (
      quoteCalculationContext === null
      || activeScreening === null
      || resolveEquoCompensoEngine(activeScreening).engine !== "dm2016_annex_i13"
      || procedure === null
      || (
        procedure.value !== "article_108_2_b_best_value"
        && procedure.value !== "article_50_1_b_direct_award"
      )
    ) return null;
    const worksValueField = quoteForm?.elements.namedItem("worksValueForExpenseLimit");
    const worksValueForExpenseLimit = worksValueField instanceof HTMLInputElement
      ? normalizeDecimalInput(worksValueField.value)
      : "";
    const bimField = quoteForm?.elements.namedItem("bimMandatory");
    return calculatePublicQuoteFinancials({
      verifiedCompensation: quoteCalculationContext.calculation.compensationRounded,
      worksValueForExpenseLimit,
      procedure: procedure.value,
      bimMandatory: bimField instanceof HTMLInputElement && bimField.checked,
      expensePercentage: adjustments.expensesPercentage,
      expenseMaximumPercentage: publicTechnicalExpenseMaximumPercentage(worksValueForExpenseLimit),
      reductionPercentage: adjustments.discountPercentage,
      socialSecurityPercentage: adjustments.socialSecurityPercentage,
      vatPercentage: adjustments.vatPercentage,
      additionalItems: adjustments.additionalItems,
    });
  };

  const refreshQuoteFinancialPreview = (): void => {
    if (quoteCalculationContext === null) return;
    try {
      const adjustments = readQuoteAdjustments();
      if (adjustments === null) return;
      const publicEconomics = calculateCurrentPublicEconomics(adjustments);
      renderQuoteFinancialPreview(
        publicEconomics === null
          ? calculateQuoteFinancials({
            ...adjustments,
            verifiedCompensation: quoteCalculationContext.calculation.compensationRounded,
          })
          : publicEconomicsToQuoteFinancials(publicEconomics),
        publicEconomics,
      );
    } catch {
      setQuotePreviewText(
        "quote-preview-base",
        formatEuro(quoteCalculationContext.calculation.compensationRounded),
      );
      setQuotePreviewText("quote-preview-grand-total", "Dati economici da correggere");
    }
  };

  const readQuoteBranding = (): QuoteBrandingInput | null => {
    if (quoteForm === null) return null;
    const value = (name: string): string => {
      const field = quoteForm.elements.namedItem(name);
      return field instanceof HTMLInputElement || field instanceof HTMLSelectElement
        ? field.value
        : "";
    };
    return {
      templateId: value("templateId") as QuoteTemplateId,
      primaryColor: value("primaryColor"),
      accentColor: value("accentColor"),
      logoDataUrl: quoteLogoDataUrl,
    };
  };

  const buildCurrentQuoteDocument = (): VersionedQuoteDocument | null => {
    const draft = readQuoteDraftInput();
    const adjustments = readQuoteAdjustments();
    const branding = readQuoteBranding();
    if (
      draft === null || adjustments === null || branding === null
      || quoteCalculationContext === null
    ) return null;
    const publicEconomics = calculateCurrentPublicEconomics(adjustments);
    return createVersionedQuoteDocument({
      draft,
      calculation: quoteCalculationContext.calculation,
      calculationDetails: quoteCalculationContext.details,
      adjustments,
      ...(publicEconomics === null
        ? {}
        : {
          publicEconomicContext: {
            procedure: publicEconomics.normalizedInput.procedure,
            bimMandatory: publicEconomics.normalizedInput.bimMandatory,
            worksValueForExpenses: publicEconomics.normalizedInput.worksValueForExpenseLimit,
          },
        }),
      branding,
    });
  };

  const updateQuoteDraft = (): void => {
    const input = readQuoteDraftInput();
    if (input === null || quotePrintButton === null || quoteValidation === null) return;
    const issues = validateQuoteDraft(input);
    currentQuoteDocument = null;

    try {
      if (quoteLogoError !== null) throw new Error(quoteLogoError);
      if (issues.length > 0) throw new Error(issues[0]);
      const quote = buildCurrentQuoteDocument();
      if (quote === null) throw new Error("Preventivo non disponibile.");
      currentQuoteDocument = quote;
      quotePrintButton.disabled = false;
      quoteValidation.classList.add("is-ready");
      quoteValidation.textContent = "Preventivo collegato al calcolo D.M. 2016 selezionato. Il PDF parametrico è disponibile.";
      const draft = quote.draft;
      const selectedProfession = document.querySelector<HTMLElement>("#selected-profession")?.textContent ?? "Professionista";
      setQuotePreviewText("quote-preview-studio", draft.studioName);
      setQuotePreviewText("quote-preview-brand", draft.studioName);
      setQuotePreviewText("quote-preview-professional", `${selectedProfession} · ${draft.professionalName}`);
      setQuotePreviewText(
        "quote-preview-sender-details",
        partyDetails(draft.senderAddress, draft.senderVatNumber),
      );
      setQuotePreviewText("quote-preview-client", draft.clientName);
      setQuotePreviewText(
        "quote-preview-address",
        partyDetails(draft.clientAddress, draft.clientVatNumber),
      );
      setQuotePreviewText("quote-preview-subject", draft.projectSubject);
      setQuotePreviewText("quote-preview-location", draft.projectLocation || "Luogo non indicato");
      setQuotePreviewText("quote-preview-issue", formatItalianCalendarDate(draft.issueDate));
      setQuotePreviewText(
        "quote-preview-expiry",
        `${draft.validityDays} giorni · fino al ${formatItalianCalendarDate(draft.expiryDate)}`,
      );
      setQuotePreviewText("quote-preview-payment", draft.paymentTerms || "Da definire");
      setQuotePreviewText("quote-preview-notes", draft.notes || "Nessuna nota inserita.");
      renderQuoteFinancialPreview(quote.financials, quote.publicEconomics);
      const quoteSheet = document.querySelector<HTMLElement>("#quote-print-sheet");
      quoteSheet?.classList.toggle("quote-sheet--editorial", quote.branding.templateId === "editorial_light");
      quoteSheet?.classList.toggle("quote-sheet--brand-header", quote.branding.templateId === "brand_header");
      quoteSheet?.style.setProperty("--quote-primary", quote.branding.primaryColor);
      quoteSheet?.style.setProperty("--quote-accent", quote.branding.accentColor);
      quoteSheet?.style.setProperty("--quote-on-primary", contrastHexColor(quote.branding.primaryColor));
      quoteSheet?.style.setProperty("--quote-on-accent", contrastHexColor(quote.branding.accentColor));
      document.querySelectorAll<HTMLOutputElement>("[data-color-output]").forEach((output) => {
        const field = output.dataset.colorOutput;
        if (field === "primaryColor") output.value = quote.branding.primaryColor;
        if (field === "accentColor") output.value = quote.branding.accentColor;
      });
      const logoFrame = document.querySelector<HTMLElement>("[data-quote-logo]");
      const logoImage = document.querySelector<HTMLImageElement>("#quote-preview-logo");
      if (logoFrame !== null) logoFrame.hidden = quote.branding.logoDataUrl === null;
      if (logoImage !== null) {
        if (quote.branding.logoDataUrl === null) logoImage.removeAttribute("src");
        else logoImage.src = quote.branding.logoDataUrl;
      }
    } catch (cause) {
      quotePrintButton.disabled = true;
      quoteValidation.classList.remove("is-ready");
      quoteValidation.textContent = cause instanceof Error
        ? cause.message
        : "Completa i campi obbligatori.";
      setQuotePreviewText("quote-preview-studio", input.studioName.trim() || "Mittente da completare");
      setQuotePreviewText("quote-preview-brand", input.studioName.trim() || "Mittente da completare");
      setQuotePreviewText("quote-preview-client", input.clientName.trim() || "Cliente da completare");
      setQuotePreviewText("quote-preview-subject", input.projectSubject.trim() || "Oggetto da completare");
    }
  };

  quoteForm?.addEventListener("input", updateQuoteDraft);
  quoteForm?.addEventListener("change", updateQuoteDraft);
  document.querySelector<HTMLInputElement>("#quote-logo")?.addEventListener("change", (event) => {
    const input = event.currentTarget;
    if (!(input instanceof HTMLInputElement)) return;
    const file = input.files?.[0];
    if (file === undefined) {
      quoteLogoDataUrl = null;
      quoteLogoError = null;
      updateQuoteDraft();
      return;
    }
    if (!new Set(["image/png", "image/jpeg"]).has(file.type)) {
      quoteLogoDataUrl = null;
      quoteLogoError = "Il logo deve essere un file PNG o JPEG.";
      updateQuoteDraft();
      return;
    }
    if (file.size > 2_000_000) {
      quoteLogoDataUrl = null;
      quoteLogoError = "Il logo supera il limite di 2 MB.";
      updateQuoteDraft();
      return;
    }
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      quoteLogoDataUrl = typeof reader.result === "string" ? reader.result : null;
      quoteLogoError = quoteLogoDataUrl === null ? "Non è stato possibile leggere il logo." : null;
      updateQuoteDraft();
    });
    reader.addEventListener("error", () => {
      quoteLogoDataUrl = null;
      quoteLogoError = "Non è stato possibile leggere il logo.";
      updateQuoteDraft();
    });
    reader.readAsDataURL(file);
  });
  quotePrintButton?.addEventListener("click", async () => {
    updateQuoteDraft();
    const boundContext = quoteCalculationContext;
    if (
      currentQuoteDocument === null
      || boundContext === null
      || currentQuoteDocument.calculation.resultSha256
        !== boundContext.calculation.resultSha256
    ) return;
    const localQuote = currentQuoteDocument;
    let quoteToExport = localQuote;
    let serverSigned = false;
    quotePrintButton.disabled = true;
    quotePrintButton.textContent = "Ricalcolo server...";
    try {
      const adjustments = readQuoteAdjustments();
      if (
        adjustments === null
        || commissioner === null
        || context === null
        || procedure === null
        || activeScreening === null
        || resolveEquoCompensoEngine(activeScreening).engine !== "dm2016_annex_i13"
      ) throw new Error("Lo screening pubblico non autorizza il preventivo D.M. 2016.");
      const bimField = quoteForm?.elements.namedItem("bimMandatory");
      const recalculation = await requestAndVerifyDm2016CanonicalQuote({
        expected: boundContext.authoritative.expectation,
        screening: {
          commissionerKind: commissioner.value as CommissionerKind,
          serviceContext: context.value as ServiceContext,
          publicAwardProcedure: procedure.value as PublicAwardProcedure,
          ...(commissioner.value === "private_enterprise" && employees?.value !== ""
            ? { previousYearEmployees: employees?.value }
            : {}),
          ...(commissioner.value === "private_enterprise" && revenue?.value !== ""
            ? { previousYearRevenueEur: revenue?.value }
            : {}),
        },
        economicInput: {
          worksValueForExpenseLimit: localQuote.publicEconomics?.normalizedInput.worksValueForExpenseLimit
            ?? "",
          bimMandatory: bimField instanceof HTMLInputElement && bimField.checked,
          expensePercentage: adjustments.expensesPercentage,
          reductionPercentage: adjustments.discountPercentage,
          socialSecurityPercentage: adjustments.socialSecurityPercentage,
          vatPercentage: adjustments.vatPercentage,
          additionalItems: adjustments.additionalItems,
        },
      });
      if (
        currentQuoteDocument !== localQuote
        || quoteCalculationContext !== boundContext
      ) {
        throw new Error("Il preventivo è cambiato durante il ricalcolo server.");
      }
      if (recalculation.status === "server_verified") {
        quoteToExport = bindVerifiedCanonicalQuote(
          localQuote,
          recalculation.document,
          recalculation.receipt,
        );
        serverSigned = true;
      }
      quotePrintButton.textContent = "Creazione del PDF...";
      const { createQuotePdf, downloadQuotePdf } = await import("./quote-pdf.js");
      const artifact = await createQuotePdf(quoteToExport);
      if (
        quoteCalculationContext === null
        || quoteToExport.calculation.resultSha256
          !== quoteCalculationContext.calculation.resultSha256
      ) {
        throw new Error("Il calcolo è cambiato durante la creazione del PDF.");
      }
      vaultLab?.setPendingQuote({
        pdfBytes: artifact.bytes,
        filename: artifact.filename,
        quote: quoteToExport,
      });
      downloadQuotePdf(artifact);
      if (quoteValidation !== null) {
        quoteValidation.classList.add("is-ready");
        quoteValidation.textContent = serverSigned
          ? "PDF creato dal risultato D.M. 2016 ricalcolato e firmato dal server. Nessun dato del mittente o del cliente è stato inviato. Il documento non costituisce una certificazione legale automatica."
          : "FACSIMILE NON VERIFICATO — NON USARE COME DOCUMENTO PROFESSIONALE. La rete o la verifica server non sono disponibili; il PDF non dichiara firma o uso professionale.";
      }
    } catch (cause) {
      if (quoteValidation !== null) {
        quoteValidation.classList.remove("is-ready");
        quoteValidation.textContent = cause instanceof Error
          ? cause.message
          : "Non è stato possibile creare il PDF.";
      }
    } finally {
      quotePrintButton.disabled = currentQuoteDocument === null;
      quotePrintButton.textContent = "Scarica il PDF sul dispositivo";
    }
  });
  renderQuoteCalculationContext();
  updateQuoteDraft();

  const commissioner = document.querySelector<HTMLSelectElement>("#screening-commissioner");
  const context = document.querySelector<HTMLSelectElement>("#screening-context");
  const procedure = document.querySelector<HTMLSelectElement>("#screening-procedure");
  const employees = document.querySelector<HTMLSelectElement>("#screening-employees");
  const revenue = document.querySelector<HTMLSelectElement>("#screening-revenue");
  const enterpriseGroup = document.querySelector<HTMLElement>("[data-screening-group='enterprise']");
  const publicProcedureGroup = document.querySelector<HTMLElement>("[data-screening-group='public-procedure']");
  let dm2016LabController: Dm2016LabController | null = null;
  let dm2016LinkedLabController: Dm2016LinkedLabController | null = null;
  let activeScreening: EquoCompensoScreeningResult | null = null;

  const updateScreening = (): void => {
    if (commissioner === null || context === null || procedure === null) return;

    const commissionerKind = commissioner.value as CommissionerKind;
    const serviceContext = context.value as ServiceContext;
    const publicAwardProcedure = serviceContext === "public_engineering_architecture_award"
      ? procedure.value as PublicAwardProcedure
      : "not_applicable";

    if (enterpriseGroup !== null) {
      enterpriseGroup.hidden = commissionerKind !== "private_enterprise";
    }
    if (publicProcedureGroup !== null) {
      publicProcedureGroup.hidden = serviceContext !== "public_engineering_architecture_award";
    }

    const input = {
      commissionerKind,
      serviceContext,
      publicAwardProcedure,
      ...(commissionerKind === "private_enterprise" && employees?.value !== ""
        ? { previousYearEmployees: employees?.value }
        : {}),
      ...(commissionerKind === "private_enterprise" && revenue?.value !== ""
        ? { previousYearRevenueEur: revenue?.value }
        : {}),
    };

    const resultElement = document.querySelector<HTMLElement>("#screening-result");
    const statusElement = document.querySelector<HTMLElement>("#screening-status");
    const titleElement = document.querySelector<HTMLElement>("#screening-result-title");
    const summaryElement = document.querySelector<HTMLElement>(".screening-result__summary");
    const rulesElement = document.querySelector<HTMLElement>("#screening-rules");
    const notesElement = document.querySelector<HTMLUListElement>("#screening-notes");
    const publicGoldenElement = document.querySelector<HTMLElement>("#public-golden");
    if (
      resultElement === null || statusElement === null || titleElement === null
      || summaryElement === null || rulesElement === null || notesElement === null
    ) return;

    try {
      const screening = classifyEquoCompenso(input);
      activeScreening = screening;
      const engineRouting = resolveEquoCompensoEngine(screening);
      dm2016LabController?.setEnabled(
        engineRouting.engine === "dm2016_annex_i13" && engineRouting.calculationAllowed,
        engineRouting.reason,
      );
      dm2016LinkedLabController?.setEnabled(
        engineRouting.engine === "dm2016_annex_i13" && engineRouting.calculationAllowed,
        engineRouting.reason,
      );
      const reductionField = quoteForm?.elements.namedItem("discountPercentage");
      const reductionLabel = document.querySelector<HTMLElement>("#quote-reduction-label");
      if (reductionField instanceof HTMLInputElement) {
        reductionField.min = "0";
        reductionField.max = screening.publicProcurementRules.directAwardMaxReductionPercent ?? "100";
      }
      if (reductionLabel !== null) {
        reductionLabel.textContent = screening.publicProcurementRules.directAwardMaxReductionPercent !== null
          ? "Riduzione affidamento diretto (massimo 20%)"
          : "Ribasso applicato esclusivamente alla quota 35%";
      }
      const tone = screeningTone(screening.applicability, screening.blockers.length > 0);
      resultElement.className = `screening-result screening-result--${tone}`;
      statusElement.textContent = screeningApplicabilityLabel(screening.applicability);
      titleElement.textContent = screeningRouteLabel(screening.route);
      summaryElement.textContent = screening.route === "blocked"
        ? "Il calcolo resta bloccato finché il percorso normativo non è determinato."
        : screening.route === "ordinary_negotiated_estimate"
        ? "Percorso ordinario negoziato separato: il motore D.M. 2016 non viene utilizzato e non è prodotta alcuna certificazione Legge 49/2023."
        : screening.route === "public_contracts_annex_i13"
          ? "La base del corrispettivo segue il percorso separato dell’art. 41 e dell’Allegato I.13. La procedura determina poi il trattamento economico applicabile."
          : screening.route === "collection_agent_adequacy_review"
            ? "La Legge 49/2023 prevede qui una regola speciale di adeguatezza: il risultato non può essere presentato come certificazione automatica."
            : "Percorso D.M. 140 in completamento: il motore D.M. 2016 non viene utilizzato come sostituto.";

      const ruleLabels: string[] = [];
      if (screening.publicProcurementRules.fixedPriceSharePercent !== null) {
        ruleLabels.push(
          `${screening.publicProcurementRules.fixedPriceSharePercent}% prezzo fisso · ${screening.publicProcurementRules.discountableSharePercent}% ribassabile`,
        );
      }
      if (screening.publicProcurementRules.directAwardMaxReductionPercent !== null) {
        ruleLabels.push(
          `Riduzione eventuale: massimo ${screening.publicProcurementRules.directAwardMaxReductionPercent}%`,
        );
      }
      rulesElement.replaceChildren(...ruleLabels.map((label) => {
        const badge = document.createElement("strong");
        badge.textContent = label;
        return badge;
      }));
      rulesElement.hidden = ruleLabels.length === 0;
      if (publicGoldenElement !== null) {
        publicGoldenElement.hidden = screening.route !== "public_contracts_annex_i13";
      }

      const notes = [...screening.reasons, ...screening.blockers];
      notesElement.replaceChildren(...notes.map((note) => {
        const item = document.createElement("li");
        item.textContent = note;
        return item;
      }));
    } catch {
      activeScreening = null;
      dm2016LabController?.setEnabled(false, "Screening non valido: nessun motore è autorizzato.");
      dm2016LinkedLabController?.setEnabled(
        false,
        "Screening non valido: nessun motore è autorizzato.",
      );
      resultElement.className = "screening-result screening-result--warning";
      statusElement.textContent = "Dati da correggere";
      titleElement.textContent = "Il controllo è stato bloccato";
      summaryElement.textContent = "Verifica le risposte inserite. Il calcolo parametrico resta separato da questa qualificazione legale.";
      rulesElement.hidden = true;
      if (publicGoldenElement !== null) publicGoldenElement.hidden = true;
      notesElement.replaceChildren(document.createElement("li"));
      const item = notesElement.firstElementChild;
      if (item !== null) item.textContent = "Le risposte sulla dimensione del committente non sono valide.";
    }
  };

  document.querySelector<HTMLFormElement>("#screening-form")?.addEventListener("input", updateScreening);
  document.querySelector<HTMLFormElement>("#screening-form")?.addEventListener("change", updateScreening);

  const dialog = document.querySelector<HTMLDialogElement>("#insurance-dialog");
  const selectDialogPanel = (selectedPanel: string): void => {
    document.querySelectorAll<HTMLButtonElement>("[data-dialog-tab]").forEach((tab) => {
      tab.setAttribute("aria-selected", String(tab.dataset.dialogTab === selectedPanel));
    });
    document.querySelectorAll<HTMLElement>("[data-dialog-panel]").forEach((panel) => {
      panel.hidden = panel.dataset.dialogPanel !== selectedPanel;
    });
    const outcome = document.querySelector<HTMLElement>(".form-outcome");
    if (outcome !== null) outcome.hidden = true;
  };

  document.querySelectorAll<HTMLButtonElement>("[data-insurance-action]").forEach((button) => {
    button.addEventListener("click", () => {
      if (dialog === null) return;
      selectDialogPanel(button.dataset.insuranceAction ?? "callback");
      dialog.showModal();
    });
  });
  document.querySelector<HTMLButtonElement>(".dialog-close")?.addEventListener("click", () => dialog?.close());
  document.querySelectorAll<HTMLButtonElement>("[data-dialog-tab]").forEach((tab) => {
    tab.addEventListener("click", () => selectDialogPanel(tab.dataset.dialogTab ?? "callback"));
  });
  const callbackInsuranceForm = document.querySelector<HTMLFormElement>("[data-insurance-form='callback']");
  const policyInsuranceForm = document.querySelector<HTMLFormElement>("[data-insurance-form='policy']");
  const insuranceTurnstile = document.querySelector<HTMLElement>("[data-insurance-turnstile]");
  const insuranceOutcome = document.querySelector<HTMLElement>(".form-outcome");
  const insuranceIntro = document.querySelector<HTMLElement>("[data-insurance-intro]");
  if (
    callbackInsuranceForm !== null && policyInsuranceForm !== null
    && insuranceTurnstile !== null && insuranceOutcome !== null && insuranceIntro !== null
  ) {
    void mountInsuranceIntake({
      callbackForm: callbackInsuranceForm,
      policyForm: policyInsuranceForm,
      turnstileRoot: insuranceTurnstile,
      outcome: insuranceOutcome,
      intro: insuranceIntro,
    });
  }

  const linkedFootnoteContext = (
    result: HashedDm2016LinkedBundleCalculationResult,
  ): Dm2016FootnoteContext => {
    const context = result.normalizedInput.footnoteContext;
    return {
      ...(context.variationAbsoluteIncrease === null
        ? {}
        : { variationAbsoluteIncrease: context.variationAbsoluteIncrease }),
      ...(context.variationAbsoluteDecrease === null
        ? {}
        : { variationAbsoluteDecrease: context.variationAbsoluteDecrease }),
      ...(context.newlyDesignedWorksGrossValue === null
        ? {}
        : { newlyDesignedWorksGrossValue: context.newlyDesignedWorksGrossValue }),
      ...(context.collaudoInCorsoDOpera === null
        ? {}
        : { collaudoInCorsoDOpera: context.collaudoInCorsoDOpera }),
      ...(context.energyDiagnosisDocumentationAvailable === null
        ? {}
        : {
          energyDiagnosisDocumentationAvailable:
            context.energyDiagnosisDocumentationAvailable,
        }),
    };
  };

  const linkedDetailRows = (
    serviceCode: string,
    result: HashedDm2016LinkedBundleCalculationResult,
  ): QuoteCalculationDetailInput["contextRows"] => {
    const context = result.normalizedInput.footnoteContext;
    if (serviceCode === "QcI.07") {
      return [
        { label: "Quantità in aumento", value: formatEuro(context.variationAbsoluteIncrease ?? "0") },
        { label: "Quantità in diminuzione", value: formatEuro(context.variationAbsoluteDecrease ?? "0") },
      ];
    }
    if (serviceCode === "QcI.08") {
      return [{
        label: "Nuove opere e nuovi prezzi",
        value: formatEuro(context.newlyDesignedWorksGrossValue ?? "0"),
      }];
    }
    if (serviceCode === "QdI.01") {
      return [{
        label: "Momento del collaudo",
        value: context.collaudoInCorsoDOpera === true
          ? "In corso d’opera · maggiorazione 20%"
          : "A lavori ultimati",
      }];
    }
    if (serviceCode === "QdI.05") {
      return [{
        label: "Diagnosi energetica",
        value: context.energyDiagnosisDocumentationAvailable === false
          ? "Non disponibile · prestazione QbII.22 aggiunta"
          : "Documentazione disponibile",
      }];
    }
    if (serviceCode === "QbII.22") {
      return [{
        label: "Motivo dell’inclusione",
        value: "Redazione della diagnosi energetica non disponibile",
      }];
    }
    return [];
  };

  const singleDetailRows = (
    result: HashedDm2016LabCalculationResult,
  ): QuoteCalculationDetailInput["contextRows"] => {
    const context = result.normalizedInput.footnoteContext;
    const rows: QuoteCalculationContextRow[] = [];
    if (context.planning !== null) {
      rows.push(
        {
          label: "Pianificazione",
          value: context.planning.level === "general"
            ? "Piano generale"
            : "Piano esecutivo o attuativo",
        },
        {
          label: "Base del valore V",
          value: ({
            territorial_gdp: "PIL del territorio interessato",
            existing_and_design_volumes: "Volumi esistenti e di progetto",
            farm_gross_saleable_production: "Produzione lorda vendibile fondiaria",
          } as const)[context.planning.valueBasis],
        },
      );
    }
    if (context.procurementModel !== null) {
      rows.push({
        label: "Modello di affidamento",
        value: context.procurementModel === "integrated"
          ? "Appalto integrato"
          : "Procedura non integrata",
      });
    }
    if (context.tenderBaseProject !== null) {
      rows.push({
        label: "Progetto a base di gara",
        value: context.tenderBaseProject ? "Sì" : "No",
      });
    }
    if (context.publicWorksConcession !== null) {
      rows.push({
        label: "Concessione di lavori pubblici",
        value: context.publicWorksConcession ? "Sì" : "No",
      });
    }
    if (
      result.service.code === "QcI.09"
      && result.reference.calculation.bands.some((band) => band.bandIndex === 1)
      && (
        result.destination.atomicScope === "EDILIZIA"
        || result.destination.atomicScope === "STRUTTURE_S01_S03"
        || result.destination.atomicScope === "STRUTTURE_S02_S04_S05_S06"
      )
    ) {
      rows.push({
        label: "QcI.09 · eccedenza € 500.000",
        value: result.reference.resolution.interpretationMode === "printed_source_strict"
          ? "Q 0,012 · testo letterale D.M. 17/06/2016; il CNI lo considera un refuso, senza rettifica normativa formale"
          : "Q 0,12 · interpretazione CNI, Circolare 893/2022; la fonte stampa 0,012 e non risulta una rettifica normativa formale",
      });
    }
    return rows;
  };

  const dm2016LabRoot = document.querySelector<HTMLElement>("#dm2016-lab-root");
  if (dm2016LabRoot !== null) {
    dm2016LabController = mountDm2016Lab(dm2016LabRoot, {
      onInvalid: () => {
        quoteCalculationContext = null;
        currentQuoteDocument = null;
        renderQuoteCalculationContext();
        updateQuoteDraft();
      },
      onResult: (labResult: HashedDm2016LabCalculationResult) => {
        if (
          activeScreening === null
          || resolveEquoCompensoEngine(activeScreening).engine !== "dm2016_annex_i13"
        ) throw new Error("Il percorso D.M. 2016 non è autorizzato dallo screening corrente.");
        quoteCalculationContext = {
          calculation: {
            inputSha256: labResult.inputSha256,
            resultSha256: labResult.resultSha256,
            engineVersion: labResult.engineVersion,
            rulesetVersion: labResult.rulesetVersion,
            compensationRounded:
              labResult.reference.calculation.compensationRounded,
          },
          authoritative: {
            kind: "single",
            expectation: {
              destinationCode: labResult.destination.code,
              serviceCode: labResult.service.code,
              valueV: labResult.normalizedInput.valueV,
              inhabitants: labResult.normalizedInput.inhabitants,
              multiplier: labResult.normalizedInput.multiplier,
              planning: labResult.normalizedInput.footnoteContext.planning,
              procurementModel:
                labResult.normalizedInput.footnoteContext.procurementModel ?? null,
              tenderBaseProject:
                labResult.normalizedInput.footnoteContext.tenderBaseProject ?? null,
              publicWorksConcession:
                labResult.normalizedInput.footnoteContext.publicWorksConcession ?? null,
              footnoteResolverVersion:
                labResult.normalizedInput.footnoteResolverVersion,
              interpretationMode:
                labResult.reference.resolution.interpretationMode,
              inputSha256: labResult.inputSha256,
              resultSha256: labResult.resultSha256,
              engineVersion: labResult.engineVersion,
              rulesetVersion: labResult.rulesetVersion,
              compensationRounded:
                labResult.reference.calculation.compensationRounded,
            },
          },
          components: [{
            label: `${labResult.service.code} · ${labResult.service.label}`,
            valueV: labResult.reference.calculation.valueV,
            compensationRounded:
              labResult.reference.calculation.compensationRounded,
          }],
          details: [{
            methodLabel: "D.M. 17 giugno 2016 · Tavole Z-1 e Z-2",
            destinationCode: labResult.destination.code,
            serviceCode: labResult.service.code,
            serviceLabel: labResult.service.label,
            valueV: labResult.reference.calculation.valueV,
            compensationRounded:
              labResult.reference.calculation.compensationRounded,
            appliedFootnotes:
              labResult.footnoteResolution.services[0]?.appliedFootnotes ?? [],
            contextRows: singleDetailRows(labResult),
          }],
          label: `D.M. 17/06/2016 · ${labResult.destination.code} · ${labResult.service.code}`,
        };
        renderQuoteCalculationContext();
        updateQuoteDraft();
      },
    });
    dm2016LinkedLabController = mountDm2016LinkedLab(dm2016LabRoot, {
      onInvalid: () => {
        quoteCalculationContext = null;
        currentQuoteDocument = null;
        renderQuoteCalculationContext();
        updateQuoteDraft();
      },
      onResult: (bundleResult: HashedDm2016LinkedBundleCalculationResult) => {
        if (
          activeScreening === null
          || resolveEquoCompensoEngine(activeScreening).engine !== "dm2016_annex_i13"
        ) throw new Error("Il percorso D.M. 2016 non è autorizzato dallo screening corrente.");
        const footnoteContext = linkedFootnoteContext(bundleResult);
        quoteCalculationContext = {
          calculation: {
            inputSha256: bundleResult.inputSha256,
            resultSha256: bundleResult.resultSha256,
            engineVersion: bundleResult.engineVersion,
            rulesetVersion: bundleResult.rulesetVersion,
            compensationRounded:
              bundleResult.compensationSummary.compensationRounded,
          },
          authoritative: {
            kind: "bundle",
            expectation: {
              destinationCode: bundleResult.destination.code,
              requestServices: bundleResult.normalizedInput.services.map((service) => ({
                serviceCode: service.serviceCode,
                valueV: service.valueV,
              })),
              footnoteContext,
              normalizedServices: bundleResult.normalizedInput.services,
              normalizedFootnoteContext:
                bundleResult.normalizedInput.footnoteContext,
              footnoteResolution: bundleResult.footnoteResolution,
              footnoteResolverVersion:
                bundleResult.normalizedInput.footnoteResolverVersion,
              interpretationMode:
                bundleResult.normalizedInput.interpretationMode,
              inputSha256: bundleResult.inputSha256,
              resultSha256: bundleResult.resultSha256,
              engineVersion: bundleResult.engineVersion,
              rulesetVersion: bundleResult.rulesetVersion,
              compensationRounded:
                bundleResult.compensationSummary.compensationRounded,
            },
          },
          components: bundleResult.references.map((reference) => ({
            label: `${reference.resolution.serviceCode} · ${bundleResult.serviceLabels[reference.resolution.serviceCode as keyof typeof bundleResult.serviceLabels]}`,
            valueV: reference.calculation.valueV,
            compensationRounded: reference.calculation.compensationRounded,
          })),
          details: bundleResult.references.map((reference, index) => ({
            methodLabel: "D.M. 17 giugno 2016 · Tavole Z-1 e Z-2",
            destinationCode: bundleResult.destination.code,
            serviceCode: reference.resolution.serviceCode,
            serviceLabel:
              bundleResult.serviceLabels[reference.resolution.serviceCode as keyof typeof bundleResult.serviceLabels],
            valueV: reference.calculation.valueV,
            compensationRounded: reference.calculation.compensationRounded,
            appliedFootnotes:
              bundleResult.footnoteResolution.services[index]?.appliedFootnotes ?? [],
            contextRows: linkedDetailRows(
              reference.resolution.serviceCode,
              bundleResult,
            ),
          })),
          label: `D.M. 17/06/2016 · ${bundleResult.destination.code} · ${bundleResult.references.length} prestazioni`,
        };
        renderQuoteCalculationContext();
        updateQuoteDraft();
      },
    });
    updateScreening();
  }

  const captureSafeAuthDraft = (): SafeAuthReturnDraft | null => {
    if (
      commissioner === null || context === null || procedure === null
      || dm2016LabController === null
    ) return null;
    const selectedProfession = document.querySelector<HTMLButtonElement>(
      ".profession.is-active",
    )?.dataset.profession;
    if (
      selectedProfession !== "Ingegnere" && selectedProfession !== "Architetto"
      && selectedProfession !== "Geometra" && selectedProfession !== "Geologo"
    ) return null;
    return {
      profession: selectedProfession,
      screening: {
        commissioner: commissioner.value as SafeAuthReturnDraft["screening"]["commissioner"],
        context: context.value as SafeAuthReturnDraft["screening"]["context"],
        procedure: procedure.value as SafeAuthReturnDraft["screening"]["procedure"],
        employees: employees?.value ?? "",
        revenue: revenue?.value ?? "",
      },
      calculation: dm2016LabController.captureDraft(),
      senderProfile: {
        studioName: readProfessionalProfileInput().studioName,
        professionalName: readProfessionalProfileInput().professionalName,
        vatNumber: readProfessionalProfileInput().vatNumber,
        address: readProfessionalProfileInput().address,
      },
      scrollY: Math.min(1_000_000, Math.max(0, Math.round(window.scrollY))),
    };
  };

  const prepareForExternalAuthRedirect = (): void => {
    const draft = captureSafeAuthDraft();
    if (draft === null) return;
    try {
      saveSafeAuthReturnDraftForBrowser(
        window.localStorage,
        window.sessionStorage,
        draft,
      );
    } catch {
      // Auth remains available even if the browser denies both storage APIs.
    }
  };

  try {
    const returnedDraft = consumeSafeAuthReturnDraftFromBrowser(
      window.localStorage,
      window.sessionStorage,
    );
    if (
      returnedDraft !== null && commissioner !== null && context !== null
      && procedure !== null
    ) {
      commissioner.value = returnedDraft.screening.commissioner;
      context.value = returnedDraft.screening.context;
      procedure.value = returnedDraft.screening.procedure;
      if (employees !== null) employees.value = returnedDraft.screening.employees;
      if (revenue !== null) revenue.value = returnedDraft.screening.revenue;
      updateScreening();
      document.querySelectorAll<HTMLButtonElement>(".profession").forEach((button) => {
        if (button.dataset.profession === returnedDraft.profession) button.click();
      });
      const restoredProfileValues: Readonly<Record<string, string>> = {
        studioName: returnedDraft.senderProfile.studioName,
        professionalName: returnedDraft.senderProfile.professionalName,
        senderVatNumber: returnedDraft.senderProfile.vatNumber,
        senderAddress: returnedDraft.senderProfile.address,
      };
      Object.entries(restoredProfileValues).forEach(([name, value]) => {
        const field = quoteField(name);
        if (field !== null) field.value = value;
      });
      dm2016LabController?.restoreDraft(returnedDraft.calculation);
      window.requestAnimationFrame(() => window.scrollTo({
        top: returnedDraft.scrollY,
        behavior: "auto",
      }));
    }
  } catch {
    // A browser that blocks both storage mechanisms still gets the calculator.
  }

  const authTrigger = document.querySelector<HTMLButtonElement>("#auth-trigger");
  const authDialog = document.querySelector<HTMLDialogElement>("#auth-dialog");
  const authVaultBridge = document.querySelector<HTMLElement>("#auth-vault-bridge");
  if (authTrigger !== null && authDialog !== null && authVaultBridge !== null) {
    void import("./auth.js").then(async ({ mountAuthControls, readPublicAuthConfig }) => {
      try {
        const config = readPublicAuthConfig(import.meta.env);
        const authController = await mountAuthControls({
          trigger: authTrigger,
          dialog: authDialog,
          vaultBridge: authVaultBridge,
          config,
          beforeExternalAuthRedirect: prepareForExternalAuthRedirect,
        });
        if (config === null) {
          vaultLab?.setCloudContext(null);
          return;
        }
        const { SupabaseFetchVaultRemote } = await import(
          "@rcprofessionale/vault-persistence/supabase-fetch"
        );
        const remote = new SupabaseFetchVaultRemote({
          supabaseUrl: config.supabaseUrl,
          publishableKey: config.publishableKey,
          getAccessToken: () => authController.getAccessToken(),
        });
        const { SupabaseProfessionalProfileRemote } = await import(
          "./professional-profile.js"
        );
        const profileRemote = new SupabaseProfessionalProfileRemote({
          supabaseUrl: config.supabaseUrl,
          publishableKey: config.publishableKey,
          getAccessToken: () => authController.getAccessToken(),
        });
        let activeProfileUserId: string | null = null;

        profileSaveButton?.addEventListener("click", () => {
          if (activeProfileUserId === null || profileStatus === null || profileSaveButton === null) return;
          const userId = activeProfileUserId;
          profileSaveButton.disabled = true;
          profileStatus.textContent = "Salvataggio dei dati del mittente in corso...";
          void profileRemote.save(userId, readProfessionalProfileInput())
            .then(() => {
              if (activeProfileUserId !== userId) return;
              profileStatus.textContent = "Dati del mittente salvati. Saranno precompilati al prossimo accesso.";
            })
            .catch((error: unknown) => {
              if (activeProfileUserId !== userId) return;
              profileStatus.textContent = error instanceof Error
                ? error.message
                : "Non è stato possibile salvare il profilo.";
            })
            .finally(() => {
              if (activeProfileUserId === userId) profileSaveButton.disabled = false;
            });
        });

        authController.subscribe((identity) => {
          vaultLab?.setCloudContext(identity === null
            ? null
            : { userId: identity.userId, remote });
          activeProfileUserId = identity?.userId ?? null;
          if (profileSaveButton !== null) profileSaveButton.disabled = identity === null;
          if (profileStatus === null) return;
          if (identity === null) {
            profileStatus.textContent = "Accedi all’area privata per salvare i dati del mittente una sola volta.";
            return;
          }
          const userId = identity.userId;
          profileStatus.textContent = "Caricamento del tuo profilo...";
          void profileRemote.load(userId)
            .then((profile) => {
              if (activeProfileUserId !== userId) return;
              if (profile === null) {
                profileStatus.textContent = "Nessun profilo salvato. Compila i tuoi dati e premi “Salva i miei dati”.";
                return;
              }
              profileStatus.textContent = applyProfessionalProfile(profile)
                ? "Profilo caricato: i dati del mittente sono stati precompilati."
                : "Profilo disponibile. I dati già inseriti non sono stati sostituiti.";
            })
            .catch(() => {
              if (activeProfileUserId === userId) {
                profileStatus.textContent = "Profilo non disponibile. Il preventivo resta utilizzabile senza salvataggio.";
              }
            });
        });
      } catch {
        authTrigger.disabled = true;
        authTrigger.textContent = "Accesso non disponibile";
        authVaultBridge.innerHTML = "<strong>Accesso bloccato in sicurezza</strong><span>La sessione non ha superato i controlli. Il calcolo e il download del PDF restano disponibili senza salvataggio online.</span>";
      }
    }).catch(() => {
      authTrigger.disabled = true;
      authTrigger.textContent = "Accesso non disponibile";
      authVaultBridge.innerHTML = "<strong>Accesso non disponibile</strong><span>Il calcolo e il download del PDF restano disponibili senza salvataggio online.</span>";
    });
  }
}

function renderError(error: unknown): void {
  const message = error instanceof Error ? error.message : "Errore sconosciuto";
  app.innerHTML = `
    <main class="fatal" id="contenuto">
      <div><span aria-hidden="true">!</span><h1>Calcolo non disponibile</h1><p>La verifica preventiva non è stata superata. Nessun risultato viene mostrato.</p><code>${escapeHtml(message)}</code></div>
    </main>`;
}

try {
  render(await buildVerificationModel());
} catch (error) {
  renderError(error);
}
